/**
 * api.mock.ts — an in-memory adapter implementing the same `Api` interface as `api.ts`,
 * so the whole web app runs before the server exists (ARCHITECTURE §9: "the web team builds
 * against api-types with a mock adapter until the real server is up, then switches by env").
 *
 * The payloads are the contract's example payloads (docs/api-personal.md §3) filled out to
 * full objects, i.e. the demo dataset of spec-personal.md §7 expressed in agorot:
 *   assets ₪194,770 = עו"ש 18,420 + פיקדון 64,000 + תיק ני"ע 112,350 (a manual balance —
 *   the securities endpoint is 404, BRIEF §9.5, so the portfolio is a `manual_asset` row and
 *   `securities.status` is `not_connected`);
 *   liabilities ₪66,000 = הלוואת רכב 38,600 + מסגרות קרדיט פתוחות 16,700 (Cal 8,400 + Cal 3,200 +
 *   Max 5,100 — ARCHITECTURE §8 counts open issuer facilities) + Max 8,310 + Cal 2,390 → net worth
 *   ₪128,770. הלוואת LabTop 52,400 is `assigned: false`, so it is outside personal net worth and
 *   inside the loans screen's `סך החוב` — that is the whole of the difference between the two.
 *   Coverage: זמין 18,420 − (Max סופי 6,840 + Cal נצבר 2,390) = 9,190.
 * Every screen therefore shows the same numbers as its detail screen, exactly like the mockup.
 *
 * DEMO DATA ONLY — never Yair's real figures. Selected by `window.__API_MOCK__` or `?mock=1`.
 */

import type {
  Account,
  AccountRef,
  Alert,
  AlertFilter,
  AlertsResponse,
  Balances,
  CardCycle,
  CardDetailResponse,
  CardSummary,
  CardsResponse,
  CategoriesResponse,
  CategoryDetailResponse,
  CategorySettings,
  CategoryStat,
  Connection,
  Coverage,
  DayString,
  GroupKey,
  LoanCardGroup,
  LoanInterest,
  LoanRow,
  LoanSort,
  LoansResponse,
  LoansTotals,
  LoanTrendPoint,
  ManualEntry,
  MonthString,
  Narrative,
  NetWorthAccount,
  NetWorthPoint,
  NetWorthResponse,
  Period,
  PeriodParam,
  Schedule,
  SettingsResponse,
  SummaryResponse,
  SyncStatusResponse,
  TransactionDetail,
  TransactionRow,
  TransactionsQuery,
  TransactionsResponse,
  UrgencySummary,
  User,
} from '../../shared-types.ts';
import { GROUP_KEYS, ALERT_FILTER_TYPES } from '../../shared-types.ts';
// REDESIGN §2/§3 are implemented once, in `shared/insight`; the demo server calls the same
// functions the real one does, so the mock home screen is bucketed and worded identically.
import { URGENCY_KIND_SPECS, buildItems, buildNarrative, countByBucket, groupByBucket } from '../../../shared/insight/src/index.ts';
import type { PersonalNarrativeInput, UrgencyInput } from '../../../shared/insight/src/index.ts';
// The Hebrew `main/sub` label map — the same source the server uses for `SubcategoryStat.label`.
import { categoryLabel } from '../../../shared/financy/src/categories.ts';
import type { Api } from './api.ts';
import { ApiError } from './api.ts';
import { addDays, addMonths, daysInMonth, monthEnd, monthOf, monthStart, monthsBetween, trailingMonths } from './dates.ts';

// ───────────────────────────── clock & constants ─────────────────────────────

const ASOF: DayString = '2026-09-06';
const CUR_MONTH: MonthString = '2026-09';
const HISTORY_FROM: DayString = '2025-09-01';
const FETCHED_AT = '2026-09-06T03:12:00.000Z'; // 06:12 Asia/Jerusalem
const CAL_FETCHED_AT = '2026-09-06T03:09:00.000Z';
const MAX_FETCHED_AT = '2026-09-06T03:14:00.000Z';
const SERVER_TIME = '2026-09-06T06:15:03.000Z';

const MONTHS12: MonthString[] = [
  '2025-09', '2025-10', '2025-11', '2025-12', '2026-01', '2026-02',
  '2026-03', '2026-04', '2026-05', '2026-06', '2026-07', '2026-08',
];

/** ₪ → agorot. */
function a(shekels: number): number {
  return Math.round(shekels * 100);
}

/** spec §7.5 — monthly spend per display group, ₪, for the 12 full months. */
const CAT_MONTHLY: Readonly<Record<GroupKey, number[]>> = {
  HOUSING: [6380, 6120, 6410, 6620, 6590, 6180, 6390, 6210, 6440, 6560, 6680, 6710],
  GROCERIES: [3520, 3610, 3480, 3790, 3560, 3420, 3980, 3660, 3540, 3610, 3720, 3770],
  KIDS: [3100, 3100, 3100, 3350, 3100, 3100, 3100, 3100, 3100, 3100, 3100, 3100],
  TRANSPORT: [2210, 2290, 2180, 2340, 2260, 2150, 2310, 2420, 2230, 2180, 2290, 2380],
  DINING: [1140, 1290, 980, 1520, 1060, 1120, 1310, 1240, 1180, 1090, 1260, 1980],
  SHOPPING: [1080, 1240, 960, 2340, 1120, 880, 1350, 1190, 1010, 1060, 1240, 1150],
  INSURANCE: [690, 690, 690, 690, 690, 690, 690, 690, 690, 690, 690, 690],
  HEALTH: [620, 710, 590, 660, 740, 610, 580, 690, 630, 660, 590, 670],
  TELECOM: [480, 480, 480, 480, 480, 480, 505, 505, 505, 505, 505, 505],
  OTHER: [720, 910, 640, 1180, 820, 690, 760, 840, 720, 780, 1420, 1560],
};

/** September 1–6 (MTD), ₪ — spec §7.5 plus the ₪38 returned-payment fee under "אחר". */
const SEP_MTD: Readonly<Record<GroupKey, number>> = {
  HOUSING: 6298, GROCERIES: 768.6, KIDS: 2350, TRANSPORT: 1460, DINING: 96.4,
  SHOPPING: 2210, INSURANCE: 340, HEALTH: 321.1, TELECOM: 183.9, OTHER: 38,
};

const INCOME_MONTHLY = [22800, 22800, 22800, 26300, 22800, 22800, 22800, 22800, 24100, 22800, 22800, 22800];

/** spec §7.2 — end-of-month balances (₪), 12 month-ends + today. */
const BAL = {
  chk: [14318, 14928, 15768, 18948, 17828, 18708, 19433, 19538, 21293, 22158, 22603, 19508, 18420],
  sav: [61480, 61480, 64000, 64000, 64000, 64000, 64000, 64000, 64000, 64000, 64000, 64000, 64000],
  sct: [88000, 90600, 93100, 96400, 97200, 99900, 100300, 103100, 106300, 107800, 110600, 111400, 112350],
  loan: [49760, 48830, 47900, 46970, 46040, 45110, 44180, 43250, 42320, 41390, 40460, 39530, 38600],
  max: [6300, 6450, 6200, 7250, 6400, 6050, 6600, 6500, 6350, 6530, 7340, 6840, 8310],
  cal: [4600, 4700, 4500, 5300, 4650, 4400, 4750, 4700, 4550, 4670, 5000, 4120, 2390],
  // The three OPEN issuer facilities assigned to this system (Cal 8,400 + Cal 3,200 + Max 5,100).
  // ARCHITECTURE §8 counts them as liabilities; leaving them out is what made `שווי נקי` report
  // ₪49,300 while the loans block beside it reported ₪107,700 with no way to reconcile the two.
  crd: [23900, 23100, 22300, 21500, 20700, 20000, 19300, 18700, 18100, 17600, 17200, 16950, 16700],
};

// ───────────────────────────── accounts & connections ─────────────────────────────

const CONN_DISCOUNT = 'conn_disc';
const CONN_MAX = 'conn_max';
const CONN_CAL = 'conn_cal';

const ACC_CHK = 'acc_chk_6516';
const ACC_CHK_BIZ = 'acc_chk_6751';
const ACC_SAV = 'acc_sav_2207';
const ACC_LOAN = 'acc_loan_9031';
const ACC_LOAN_BIZ = 'acc_loan_4470';
const ACC_MAX = 'acc_max_6044';
const ACC_CAL = 'acc_cal_2391';

function balances(over: Partial<Balances>): Balances {
  return {
    booked: null, bookedInclLimit: null, forwardAvailable: null, expected: null,
    interimBooked: null, interimAvailable: null, referenceDate: ASOF, ...over,
  };
}

interface MockAccount extends Account {
  /** index into BAL for the month-end history, or null. */
  balKey: keyof typeof BAL | null;
  closedNote: string | null;
}

function accountRef(acc: MockAccount): AccountRef {
  return {
    id: acc.id, type: acc.type, providerId: acc.providerId, connectionId: acc.connectionId,
    name: acc.name, numberLast4: acc.numberLast4, creditStatus: acc.creditStatus,
    connectionStatus: acc.connectionStatus, dataAsOf: acc.dataAsOf,
  };
}

function mkAccount(over: Partial<MockAccount> & Pick<MockAccount, 'id' | 'type' | 'providerId' | 'connectionId' | 'name'>): MockAccount {
  return {
    numberLast4: null, creditStatus: null, connectionStatus: 'ACTIVE', dataAsOf: null,
    currency: 'ILS', product: null, balances: balances({}), creditLimitAgorot: null,
    overdraftLimitAgorot: null, cardDueDay: null, loanType: null, contractStart: null, contractEnd: null,
    interest: null, canonical: true, assigned: true, note: null, txCount: 0,
    firstSeen: '2026-09-06T03:12:40.000Z', updatedAt: '2026-09-06T03:12:40.000Z',
    displayBalanceAgorot: null, displayBalanceKind: 'none', balKey: null, closedNote: null,
    ...over,
  };
}

const accountsData: MockAccount[] = [
  mkAccount({
    id: ACC_CHK, type: 'CHECKING', providerId: 'discount', connectionId: CONN_DISCOUNT, name: 'עו"ש דיסקונט',
    numberLast4: '6516', balKey: 'chk', overdraftLimitAgorot: a(10000), txCount: 425,
    balances: balances({ booked: a(18420), bookedInclLimit: a(28420), forwardAvailable: a(18420) }),
    displayBalanceAgorot: a(18420), displayBalanceKind: 'available',
  }),
  mkAccount({
    id: ACC_CHK_BIZ, type: 'CHECKING', providerId: 'discount', connectionId: CONN_DISCOUNT, name: 'עו"ש דיסקונט',
    numberLast4: '6751', assigned: false, overdraftLimitAgorot: a(20000), txCount: 0,
    product: 'החשבון העסקי של LabTop',
    balances: balances({ booked: a(41230), bookedInclLimit: a(61230), forwardAvailable: a(41230) }),
    displayBalanceAgorot: a(41230), displayBalanceKind: 'available',
  }),
  mkAccount({
    id: ACC_SAV, type: 'SAVINGS', providerId: 'discount', connectionId: CONN_DISCOUNT, name: 'פיקדון שקלי מתחדש',
    numberLast4: '2207', balKey: 'sav', interest: { type: 'FIXD', ratePct: 4.1, relatedIndex: null },
    balances: balances({ booked: a(64000), expected: a(64000) }),
    displayBalanceAgorot: a(64000), displayBalanceKind: 'booked',
    note: { maturityDate: '2026-11-15', remainingPayments: null, monthlyPaymentAgorot: null, note: null, updatedBy: 'יאיר', updatedAt: '2026-09-01T10:00:00.000Z' },
  }),
  mkAccount({
    id: ACC_LOAN, type: 'LOAN', providerId: 'discount', connectionId: CONN_DISCOUNT, name: 'רכב בתשלומים',
    numberLast4: '9031', balKey: 'loan', loanType: 'RETAIL_LOAN', contractEnd: '2029-09-05',
    interest: { type: 'FIXD', ratePct: 6.4, relatedIndex: null },
    balances: balances({ booked: a(38600) }), displayBalanceAgorot: a(38600), displayBalanceKind: 'booked',
    note: { maturityDate: null, remainingPayments: 36, monthlyPaymentAgorot: a(1180), note: null, updatedBy: 'יאיר', updatedAt: '2026-09-01T10:00:00.000Z' },
  }),
  mkAccount({
    id: ACC_LOAN_BIZ, type: 'LOAN', providerId: 'discount', connectionId: CONN_DISCOUNT, name: 'הלוואה ללקוחות עסקיים',
    numberLast4: '4470', assigned: false, loanType: 'RETAIL_LOAN', contractEnd: '2028-04-01',
    interest: { type: 'INDE', ratePct: 8.9, relatedIndex: 'prime' },
    balances: balances({ booked: a(52400) }), displayBalanceAgorot: a(52400), displayBalanceKind: 'booked',
  }),
  mkAccount({
    id: 'acc_cal_cr_2391', type: 'LOAN', providerId: 'cal', connectionId: CONN_CAL, name: 'קרדיט בכרטיס',
    numberLast4: '2391', loanType: 'CREDIT_CARD', contractStart: '2025-04-15', contractEnd: '2027-03-15',
    product: 'Cal קרדיט', interest: { type: 'INDE', ratePct: 11.9, relatedIndex: 'prime' },
    connectionStatus: 'EXPIRED', dataAsOf: CAL_FETCHED_AT,
    balances: balances({ booked: a(8400) }), displayBalanceAgorot: a(8400), displayBalanceKind: 'booked',
  }),
  mkAccount({
    id: 'acc_cal_cr_2392', type: 'LOAN', providerId: 'cal', connectionId: CONN_CAL, name: 'תשלומים — מוצרי חשמל',
    numberLast4: '2391', loanType: 'CREDIT_CARD', contractStart: '2025-11-10', contractEnd: '2027-01-10',
    product: 'Cal תשלומים', interest: { type: 'FIXD', ratePct: 9.5, relatedIndex: null },
    connectionStatus: 'EXPIRED', dataAsOf: CAL_FETCHED_AT,
    balances: balances({ booked: a(3200) }), displayBalanceAgorot: a(3200), displayBalanceKind: 'booked',
  }),
  mkAccount({
    id: 'acc_cal_cr_0182', type: 'LOAN', providerId: 'cal', connectionId: CONN_CAL, name: 'קרדיט ישן',
    numberLast4: '0182', loanType: 'CREDIT_CARD', contractStart: '2023-02-01', contractEnd: '2025-12-01',
    product: 'Cal קרדיט', interest: { type: 'FIXD', ratePct: 10.8, relatedIndex: null },
    connectionStatus: 'EXPIRED', dataAsOf: CAL_FETCHED_AT,
    balances: balances({ booked: a(1450) }), displayBalanceAgorot: a(1450), displayBalanceKind: 'booked',
  }),
  mkAccount({
    id: 'acc_max_cr_6044', type: 'LOAN', providerId: 'max', connectionId: CONN_MAX, name: 'קרדיט Max',
    numberLast4: '6044', loanType: 'CREDIT_CARD', contractStart: '2025-06-05', contractEnd: '2026-12-05',
    product: 'Max קרדיט', interest: { type: 'INDE', ratePct: 10.4, relatedIndex: 'prime' },
    balances: balances({ booked: a(5100) }), displayBalanceAgorot: a(5100), displayBalanceKind: 'booked',
  }),
  mkAccount({
    id: 'acc_disc_overdraft', type: 'LOAN', providerId: 'discount', connectionId: CONN_DISCOUNT, name: 'מסגרת אשראי',
    numberLast4: '6516', loanType: 'CREDIT_LIMIT', product: 'מסגרת עו"ש',
    balances: balances({ booked: a(10000) }), displayBalanceAgorot: a(10000), displayBalanceKind: 'booked',
  }),
  mkAccount({
    id: ACC_MAX, type: 'CARD', providerId: 'max', connectionId: CONN_MAX, name: 'Max', numberLast4: '6044',
    creditStatus: 'enabled', product: 'דיסקונט בינלאומי', creditLimitAgorot: a(14000), balKey: 'max', txCount: 828,
    balances: balances({ booked: a(8310), interimBooked: a(8310), interimAvailable: a(5690) }),
    displayBalanceAgorot: a(8310), displayBalanceKind: 'booked',
  }),
  mkAccount({
    id: ACC_CAL, type: 'CARD', providerId: 'cal', connectionId: CONN_CAL, name: 'Cal', numberLast4: '2391',
    creditStatus: 'enabled', product: 'Cal Visa', creditLimitAgorot: a(100000), cardDueDay: 10, balKey: 'cal', txCount: 350,
    connectionStatus: 'EXPIRED', dataAsOf: CAL_FETCHED_AT,
    balances: balances({ booked: a(2390), interimBooked: a(2390), interimAvailable: a(97610) }),
    displayBalanceAgorot: a(2390), displayBalanceKind: 'booked',
  }),
  mkAccount({
    id: 'acc_max_1207', type: 'CARD', providerId: 'max', connectionId: CONN_MAX, name: 'Max', numberLast4: '1207',
    creditStatus: 'blocked', product: 'Max Business', creditLimitAgorot: a(5000), txCount: 214,
    closedNote: 'חסום מ-03.2026 · הוחלף בכרטיס ****6044', balances: balances({ booked: 0 }),
    displayBalanceAgorot: 0, displayBalanceKind: 'booked',
  }),
  mkAccount({
    id: 'acc_cal_0182', type: 'CARD', providerId: 'cal', connectionId: CONN_CAL, name: 'Cal', numberLast4: '0182',
    creditStatus: 'blocked', product: 'Cal Mastercard', creditLimitAgorot: a(20000), cardDueDay: 10, txCount: 96,
    connectionStatus: 'EXPIRED', dataAsOf: CAL_FETCHED_AT,
    closedNote: 'חסום — אבד ודווח 11.2025', balances: balances({ booked: 0 }),
    displayBalanceAgorot: 0, displayBalanceKind: 'booked',
  }),
  mkAccount({
    id: 'acc_max_3350', type: 'CARD', providerId: 'max', connectionId: CONN_MAX, name: 'Max', numberLast4: '3350',
    creditStatus: 'deleted', txCount: 58, closedNote: 'נסגר 2024', balances: balances({ booked: 0 }),
    displayBalanceAgorot: 0, displayBalanceKind: 'booked',
  }),
  mkAccount({
    id: 'acc_cal_4416', type: 'CARD', providerId: 'cal', connectionId: CONN_CAL, name: 'Cal', numberLast4: '4416',
    creditStatus: 'deleted', txCount: 41, closedNote: 'הוחלף (תוקף) 05.2025', connectionStatus: 'EXPIRED', dataAsOf: CAL_FETCHED_AT,
    balances: balances({ booked: 0 }), displayBalanceAgorot: 0, displayBalanceKind: 'booked',
  }),
  mkAccount({
    id: 'acc_disc_5561', type: 'CARD', providerId: 'discount', connectionId: CONN_DISCOUNT, name: 'דיסקונט', numberLast4: '5561',
    creditStatus: 'deleted', txCount: 12, closedNote: 'כרטיס בנק ישן — נסגר 2024',
    balances: balances({ booked: 0 }), displayBalanceAgorot: 0, displayBalanceKind: 'booked',
  }),
];

const byId = new Map(accountsData.map((acc) => [acc.id, acc]));
function acc(id: string): MockAccount {
  const found = byId.get(id);
  if (!found) throw new ApiError('NOT_FOUND', 'חשבון לא נמצא', 404);
  return found;
}

const connectionsData: Connection[] = [
  {
    id: CONN_DISCOUNT, providerId: 'discount', status: 'ACTIVE', mode: 'PSD2', startDate: '2025-09-06',
    expiryDate: '2027-02-08', lastFetchedAt: FETCHED_AT, lastFetchedDataDate: ASOF,
    restrictedTo: ['CACC', 'SVGS', 'LOAN', 'CARD'],
    counts: { accounts: 5, cards: 1, savings: 2, loans: 9, securities: 0, transactions: 2931 },
    accountIds: [ACC_CHK, ACC_CHK_BIZ, ACC_SAV, ACC_LOAN, ACC_LOAN_BIZ, 'acc_disc_5561'],
    assignedAccountCount: 4, maskedPans: [], needsRelink: false,
  },
  {
    id: CONN_MAX, providerId: 'max', status: 'ACTIVE', mode: 'PSD2', startDate: '2025-09-06',
    expiryDate: '2027-02-08', lastFetchedAt: MAX_FETCHED_AT, lastFetchedDataDate: ASOF,
    restrictedTo: ['CARD', 'LOAN'],
    counts: { accounts: 3, cards: 3, savings: 0, loans: 2, securities: 0, transactions: 1100 },
    accountIds: [ACC_MAX, 'acc_max_1207', 'acc_max_3350'], assignedAccountCount: 3,
    maskedPans: ['****6044'], needsRelink: false,
  },
  {
    id: CONN_CAL, providerId: 'cal', status: 'EXPIRED', mode: 'PSD2', startDate: '2025-09-06',
    expiryDate: '2026-09-06', lastFetchedAt: CAL_FETCHED_AT, lastFetchedDataDate: ASOF,
    restrictedTo: ['CARD', 'LOAN'],
    counts: { accounts: 4, cards: 4, savings: 0, loans: 29, securities: 0, transactions: 689 },
    accountIds: [ACC_CAL, 'acc_cal_0182', 'acc_cal_4416'], assignedAccountCount: 3,
    maskedPans: ['****2391'], needsRelink: true,
  },
  {
    id: 'conn_disc_2', providerId: 'discount', status: 'FETCHING', mode: 'PSD2', startDate: '2026-09-06',
    expiryDate: '2027-03-01', lastFetchedAt: null, lastFetchedDataDate: null, restrictedTo: ['CACC'],
    counts: { accounts: 0, cards: 0, savings: 0, loans: 0, securities: 0, transactions: 0 },
    accountIds: [], assignedAccountCount: 0, maskedPans: [], needsRelink: false,
  },
];

const usersData: User[] = [
  { id: 'u_01', email: 'yair126@gmail.com', displayName: 'יאיר', role: 'owner', createdAt: '2026-09-01T10:00:00.000Z', lastLoginAt: '2026-09-06T05:40:12.000Z' },
  { id: 'u_02', email: 'michal@example.com', displayName: 'מיכל', role: 'member', createdAt: '2026-09-01T10:05:00.000Z', lastLoginAt: '2026-09-05T18:10:00.000Z' },
];

// ───────────────────────────── transactions ─────────────────────────────

let seq = 0;
function txId(): string {
  seq += 1;
  return `01J7Q3Z8K2M4N6P8R0T2V${String(seq).padStart(5, '0')}`;
}

interface TxSeed {
  day: DayString;
  accountId: string;
  merchant: string;
  amount: number; // ₪, signed
  group: GroupKey;
  main: string;
  sub: string;
  scope: TransactionRow['scope'];
  status?: TransactionRow['status'];
  installment?: { n: number; total: number };
  orig?: { minor: number; currency: string };
  returnKind?: TransactionRow['returnKind'];
  balanceAfter?: number;
}

function makeTx(seed: TxSeed): TransactionRow {
  const account = acc(seed.accountId);
  const amountAgorot = a(seed.amount);
  const isCard = account.type === 'CARD';
  return {
    id: txId(),
    account: accountRef(account),
    kind: isCard ? 'CARD' : 'CHECKING',
    status: seed.status ?? 'BOOKED',
    effDate: seed.day,
    txDate: isCard ? seed.day : null,
    bookingDate: seed.status === 'PENDING' ? null : isCard ? addDays(seed.day, 2) : seed.day,
    valueDate: seed.day,
    amountAgorot,
    amountMinor: amountAgorot,
    currency: 'ILS',
    origAmountMinor: seed.orig ? seed.orig.minor : null,
    origCurrency: seed.orig ? seed.orig.currency : null,
    merchant: seed.merchant,
    merchantKey: seed.merchant.replace(/[\d\p{P}]/gu, '').trim().toLowerCase(),
    description: seed.merchant,
    category: { main: seed.main, sub: seed.sub },
    groupKey: seed.group,
    groupSource: 'mapped',
    scope: seed.scope,
    installment: seed.installment ? { n: seed.installment.n, total: seed.installment.total } : null,
    balanceEndDayAgorot: seed.balanceAfter === undefined ? null : a(seed.balanceAfter),
    classificationType: seed.scope === 'income' ? 'REGULAR_INCOME' : 'VARIABLE_EXPENSE',
    returnKind: seed.returnKind ?? null,
    linkedCardAccountId: seed.scope === 'card-charge' ? ACC_CAL : null,
    transferPairId: null,
    note: null,
    internal: false,
    userCategory: null,
    superseded: false,
    supersededBy: null,
    manual: false,
  };
}

/** September 1–6 — spec §7.6 verbatim (+ the returned standing order of BRIEF §9.3). */
const SEPTEMBER: TxSeed[] = [
  { day: '2026-09-01', accountId: ACC_CHK, merchant: 'מעסיק — משכורת', amount: 13200, group: 'OTHER', main: 'INCOMES_EXPENSES', sub: 'OTHER', scope: 'income', balanceAfter: 32620 },
  { day: '2026-09-01', accountId: ACC_CHK, merchant: 'שכירות — העברה לבעל הדירה', amount: -5200, group: 'HOUSING', main: 'HOUSEHOLD_&_SERVICES', sub: 'UTILITIES', scope: 'spend', balanceAfter: 27420 },
  { day: '2026-09-02', accountId: ACC_CHK, merchant: 'כאל — חיוב כרטיס', amount: -4120, group: 'OTHER', main: 'INCOMES_EXPENSES', sub: 'CREDIT_CARD_CHECKING', scope: 'card-charge', balanceAfter: 23300 },
  { day: '2026-09-02', accountId: ACC_CHK, merchant: 'עיריית תל אביב — ארנונה', amount: -612, group: 'HOUSING', main: 'HOUSEHOLD_&_SERVICES', sub: 'UTILITIES', scope: 'spend', balanceAfter: 22688 },
  { day: '2026-09-02', accountId: ACC_MAX, merchant: 'שופרסל דיל', amount: -412.6, group: 'GROCERIES', main: 'FOOD_&_DRINKS', sub: 'GROCERIES', scope: 'spend' },
  { day: '2026-09-03', accountId: ACC_CHK, merchant: 'חברת החשמל', amount: -486, group: 'HOUSING', main: 'HOUSEHOLD_&_SERVICES', sub: 'UTILITIES', scope: 'spend', balanceAfter: 22202 },
  { day: '2026-09-03', accountId: ACC_MAX, merchant: 'פז — תחנת דלק', amount: -280, group: 'TRANSPORT', main: 'TRANSPORT', sub: 'CAR_&_FUEL', scope: 'spend' },
  { day: '2026-09-03', accountId: ACC_CAL, merchant: 'זארה', amount: -320, group: 'SHOPPING', main: 'SHOPPING', sub: 'CLOTHES_&_ACCESSORIES', scope: 'spend' },
  { day: '2026-09-04', accountId: ACC_MAX, merchant: 'וולט', amount: -96.4, group: 'DINING', main: 'FOOD_&_DRINKS', sub: 'RESTAURANT', scope: 'spend' },
  { day: '2026-09-04', accountId: ACC_CAL, merchant: 'איקאה', amount: -1890, group: 'SHOPPING', main: 'SHOPPING', sub: 'SHOPPING_OTHER', scope: 'spend' },
  { day: '2026-09-05', accountId: ACC_CHK, merchant: 'דיסקונט — החזר הלוואת רכב', amount: -1180, group: 'TRANSPORT', main: 'LOAN_TRANSACTION', sub: 'LOAN', scope: 'spend', balanceAfter: 21022 },
  { day: '2026-09-05', accountId: ACC_CHK, merchant: 'ביט — גן "שיבולים"', amount: -2350, group: 'KIDS', main: 'OTHER', sub: 'EDUCATION', scope: 'spend', balanceAfter: 18672 },
  { day: '2026-09-05', accountId: ACC_MAX, merchant: 'רמי לוי', amount: -356, group: 'GROCERIES', main: 'FOOD_&_DRINKS', sub: 'GROCERIES', scope: 'spend' },
  { day: '2026-09-05', accountId: ACC_MAX, merchant: 'מכבי שירותי בריאות', amount: -141.1, group: 'HEALTH', main: 'HEALTH_&_BEAUTY', sub: 'HEALTHCARE', scope: 'spend' },
  { day: '2026-09-06', accountId: ACC_CHK, merchant: 'ביטוח ישיר — רכב', amount: -340, group: 'INSURANCE', main: 'INSURANCE', sub: 'INSURANCE', scope: 'spend', balanceAfter: 18332 },
  { day: '2026-09-06', accountId: ACC_CHK, merchant: 'ביטוח ישיר — הוראת קבע שחזרה', amount: 340, group: 'OTHER', main: 'HOK_RETURN', sub: 'FUNDS_NOT_COVER', scope: 'fee-return', returnKind: 'principal', balanceAfter: 18672 },
  { day: '2026-09-06', accountId: ACC_CHK, merchant: 'עמלת החזרת הוראת קבע', amount: -38, group: 'OTHER', main: 'HOK_RETURN', sub: 'HOK_AKAM_COMMISSION', scope: 'spend', returnKind: 'fee', balanceAfter: 18634 },
  { day: '2026-09-06', accountId: ACC_MAX, merchant: 'נטפליקס', amount: -54.9, group: 'TELECOM', main: 'HOUSEHOLD_&_SERVICES', sub: 'COMMUNICATIONS', scope: 'spend', orig: { minor: -1499, currency: 'USD' } },
  { day: '2026-09-06', accountId: ACC_MAX, merchant: 'סלקום', amount: -129, group: 'TELECOM', main: 'HOUSEHOLD_&_SERVICES', sub: 'COMMUNICATIONS', scope: 'spend' },
  { day: '2026-09-06', accountId: ACC_CAL, merchant: 'סופר-פארם אונליין', amount: -180, group: 'HEALTH', main: 'HEALTH_&_BEAUTY', sub: 'PHARMACY', scope: 'spend', status: 'PENDING' },
];

/** Merchant pools per group for the generated history (mockup-rules.md demo list). */
const POOLS: Readonly<Record<GroupKey, Array<[string, string, string, string]>>> = {
  HOUSING: [
    ['שכירות — העברה לבעל הדירה', 'HOUSEHOLD_&_SERVICES', 'UTILITIES', ACC_CHK],
    ['חברת החשמל', 'HOUSEHOLD_&_SERVICES', 'UTILITIES', ACC_CHK],
    ['עיריית תל אביב — ארנונה', 'HOUSEHOLD_&_SERVICES', 'UTILITIES', ACC_CHK],
  ],
  GROCERIES: [
    ['שופרסל דיל', 'FOOD_&_DRINKS', 'GROCERIES', ACC_MAX],
    ['רמי לוי', 'FOOD_&_DRINKS', 'GROCERIES', ACC_MAX],
    ['ויקטורי', 'FOOD_&_DRINKS', 'GROCERIES', ACC_CAL],
  ],
  KIDS: [
    ['ביט — גן "שיבולים"', 'OTHER', 'EDUCATION', ACC_CHK],
    ['חוג כדורסל', 'OTHER', 'EDUCATION', ACC_CHK],
    ['ציוד לבית הספר', 'OTHER', 'EDUCATION', ACC_MAX],
  ],
  TRANSPORT: [
    ['דיסקונט — החזר הלוואת רכב', 'LOAN_TRANSACTION', 'LOAN', ACC_CHK],
    ['פז — תחנת דלק', 'TRANSPORT', 'CAR_&_FUEL', ACC_MAX],
    ['סונול', 'TRANSPORT', 'CAR_&_FUEL', ACC_CAL],
  ],
  DINING: [
    ['וולט', 'FOOD_&_DRINKS', 'RESTAURANT', ACC_MAX],
    ['קפה לנדוור', 'FOOD_&_DRINKS', 'COFFEE_&_SNACKS', ACC_MAX],
    ['מסעדת הבית', 'FOOD_&_DRINKS', 'RESTAURANT', ACC_CAL],
  ],
  SHOPPING: [
    ['איקאה', 'SHOPPING', 'SHOPPING_OTHER', ACC_CAL],
    ['זארה', 'SHOPPING', 'CLOTHES_&_ACCESSORIES', ACC_CAL],
    ['KSP', 'SHOPPING', 'ELECTRONICS', ACC_MAX],
  ],
  INSURANCE: [
    ['ביטוח ישיר — רכב', 'INSURANCE', 'INSURANCE', ACC_CHK],
    ['הראל — ביטוח בריאות', 'INSURANCE', 'INSURANCE', ACC_CHK],
    ['מגדל — ביטוח דירה', 'INSURANCE', 'INSURANCE', ACC_CHK],
  ],
  HEALTH: [
    ['מכבי שירותי בריאות', 'HEALTH_&_BEAUTY', 'HEALTHCARE', ACC_MAX],
    ['סופר-פארם', 'HEALTH_&_BEAUTY', 'PHARMACY', ACC_MAX],
    ['אופטיקה הלפרין', 'HEALTH_&_BEAUTY', 'EYECARE', ACC_CAL],
  ],
  TELECOM: [
    ['סלקום', 'HOUSEHOLD_&_SERVICES', 'COMMUNICATIONS', ACC_MAX],
    ['נטפליקס', 'HOUSEHOLD_&_SERVICES', 'COMMUNICATIONS', ACC_MAX],
    ['פרטנר TV', 'HOUSEHOLD_&_SERVICES', 'COMMUNICATIONS', ACC_CHK],
  ],
  OTHER: [
    ['עמלות בנק', 'FINANCE', 'FEES', ACC_CHK],
    ['מתנות', 'OTHER', 'OTHER', ACC_CAL],
    ['חיות מחמד — פטשופ', 'OTHER', 'PETS', ACC_MAX],
  ],
};

/** Deterministic split of a monthly total into 3 rows that sum EXACTLY to the total. */
function split3(total: number, salt: number): [number, number, number] {
  const first = Math.round(total * (0.42 + ((salt % 7) - 3) / 100) * 100) / 100;
  const second = Math.round(total * (0.33 + ((salt % 5) - 2) / 100) * 100) / 100;
  const third = Math.round((total - first - second) * 100) / 100;
  return [first, second, third];
}

function buildHistory(): TransactionRow[] {
  const rows: TransactionRow[] = [];
  MONTHS12.forEach((month, mi) => {
    const [year, mon] = month.split('-').map(Number);
    const dim = daysInMonth(year ?? 2026, mon ?? 1);
    // income
    rows.push(makeTx({ day: `${month}-01`, accountId: ACC_CHK, merchant: 'מעסיק — משכורת', amount: 13200, group: 'OTHER', main: 'INCOMES_EXPENSES', sub: 'OTHER', scope: 'income' }));
    rows.push(makeTx({ day: `${month}-10`, accountId: ACC_CHK, merchant: 'LabTop — משיכת בעלים', amount: 9600, group: 'OTHER', main: 'INCOMES_EXPENSES', sub: 'OTHER', scope: 'income' }));
    const extra = (INCOME_MONTHLY[mi] ?? 22800) - 22800;
    if (extra > 0) {
      rows.push(makeTx({
        day: `${month}-${month === '2025-12' ? '20' : '12'}`, accountId: ACC_CHK,
        merchant: month === '2025-12' ? 'מעסיק — בונוס' : 'רשות המסים — החזר מס',
        amount: extra, group: 'OTHER', main: 'INCOMES_EXPENSES', sub: 'OTHER', scope: 'income',
      }));
    }
    // card charge of the previous cycle
    rows.push(makeTx({
      day: `${month}-${String(Math.min(10, dim)).padStart(2, '0')}`, accountId: ACC_CHK, merchant: 'מקס איט — חיוב כרטיס',
      amount: -(BAL.max[mi] ?? 6500), group: 'OTHER', main: 'INCOMES_EXPENSES', sub: 'CREDIT_CARD_CHECKING', scope: 'card-charge',
    }));
    // spend rows, exactly summing to the month's category total
    for (const key of GROUP_KEYS) {
      const total = CAT_MONTHLY[key][mi] ?? 0;
      if (!total) continue;
      const pool = POOLS[key];
      const amounts = split3(total, mi + key.length);
      amounts.forEach((amount, idx) => {
        if (amount <= 0) return;
        const entry = pool[idx] ?? pool[0];
        if (!entry) return;
        const day = Math.min(dim, 3 + idx * 9 + (mi % 5));
        rows.push(makeTx({
          day: `${month}-${String(day).padStart(2, '0')}`,
          accountId: entry[3], merchant: entry[0], amount: -amount, group: key,
          main: entry[1], sub: entry[2], scope: 'spend',
          installment: key === 'SHOPPING' && idx === 2 && mi >= 9 ? { n: mi - 8, total: 12 } : undefined,
        }));
      });
    }
  });
  return rows;
}

const transactionsData: TransactionRow[] = [...buildHistory(), ...SEPTEMBER.map(makeTx)].sort((x, y) =>
  x.effDate < y.effDate ? 1 : x.effDate > y.effDate ? -1 : x.id < y.id ? 1 : -1,
);

// ───────────────────────────── mutable demo state ─────────────────────────────

const state = {
  user: usersData[0] as User,
  appearance: { theme: 'auto' as const, textSize: 'normal' as const, exactNumbers: false },
  alertRules: { cardDueDays: 5, thresholdPct: 20, largeTxAgorot: a(1500), staleHours: 30, disabled: [] as never[] },
  categorySettings: new Map<GroupKey, CategorySettings>(),
  cardSettings: new Map<string, { cutoffDay: number; dueDay: number }>([
    [ACC_MAX, { cutoffDay: 1, dueDay: 10 }],
    [ACC_CAL, { cutoffDay: 25, dueDay: 10 }],
  ]),
  overrides: new Map<string, GroupKey>(),
  notes: new Map<string, string>(),
  internal: new Map<string, boolean>(),
  alertState: new Map<string, { readAt: string | null; dismissedAt: string | null }>(),
  syncRunId: 41,
  creditsUsed: 88,
};

// ───────────────────────────── period ─────────────────────────────

function makePeriod(from: DayString, to: DayString, key: Period['key'], prev: { from: DayString; to: DayString } | null): Period {
  const months = monthsBetween(from, to);
  const lastMonth = months[months.length - 1] ?? monthOf(to);
  const lastFrom = monthStart(lastMonth);
  const end = monthEnd(lastMonth);
  const lastTo = to < end ? to : end;
  return {
    key, from, to, months, lastMonth, lastFrom, lastTo,
    multi: months.length > 1,
    partial: to === ASOF && lastTo < end,
    isMonth: months.length === 1,
    prev, historyFrom: HISTORY_FROM,
  };
}

function resolvePeriod(param?: PeriodParam): Period {
  const value = param ?? 'month';
  if (value === 'month' || value === CUR_MONTH) {
    return makePeriod('2026-09-01', ASOF, 'month', { from: '2026-08-01', to: '2026-08-06' });
  }
  if (value === 'prev') return makePeriod('2026-08-01', '2026-08-31', 'prev', { from: '2026-07-01', to: '2026-07-31' });
  if (value === '3m') return makePeriod('2026-06-01', '2026-08-31', '3m', { from: '2026-03-01', to: '2026-05-31' });
  if (value === '12m') return makePeriod('2025-09-01', '2026-08-31', '12m', null);
  if (/^\d{4}-\d{2}$/.test(value)) {
    if (value > CUR_MONTH) throw new ApiError('INVALID_PERIOD', 'התקופה שנבחרה אינה תקינה.', 400, { period: value });
    const prevMonth = addMonths(value, -1);
    return makePeriod(monthStart(value), monthEnd(value), 'prev', { from: monthStart(prevMonth), to: monthEnd(prevMonth) });
  }
  const [from, to] = value.split(',');
  if (!from || !to) throw new ApiError('INVALID_PERIOD', 'התקופה שנבחרה אינה תקינה.', 400, { period: value });
  const clamped = to > ASOF ? ASOF : to;
  if (from > clamped) throw new ApiError('INVALID_PERIOD', 'התקופה שנבחרה אינה תקינה.', 400, { period: value });
  const len = Math.max(1, Math.round((Date.parse(clamped) - Date.parse(from)) / 86400000) + 1);
  if (len > 366) throw new ApiError('PERIOD_TOO_LONG', 'טווח מותאם מוגבל ל-12 חודשים.', 400, { days: len });
  const prevTo = addDays(from, -1);
  const prevFrom = addDays(prevTo, -(len - 1));
  return makePeriod(from, clamped, 'custom', prevFrom >= HISTORY_FROM ? { from: prevFrom, to: prevTo } : null);
}

// ───────────────────────────── derived metrics ─────────────────────────────

function groupOf(row: TransactionRow): GroupKey {
  return state.overrides.get(row.id) ?? row.groupKey;
}

function isInternal(row: TransactionRow): boolean {
  return state.internal.get(row.id) ?? row.internal;
}

function decorate(row: TransactionRow): TransactionRow {
  const override = state.overrides.get(row.id);
  const note = state.notes.get(row.id) ?? null;
  const internal = isInternal(row);
  return {
    ...row,
    groupKey: override ?? row.groupKey,
    groupSource: override ? 'user' : row.groupSource,
    scope: internal ? 'transfer' : row.scope,
    internal,
    note,
    userCategory: override ? { by: state.user.displayName, at: SERVER_TIME, viaMerchantRule: false } : null,
  };
}

function rowsIn(from: DayString, to: DayString): TransactionRow[] {
  return transactionsData.filter((row) => row.effDate >= from && row.effDate <= to);
}

function spendIn(from: DayString, to: DayString, group?: GroupKey): number {
  let sum = 0;
  for (const row of rowsIn(from, to)) {
    const decorated = decorate(row);
    if (decorated.scope !== 'spend' || decorated.amountAgorot === null) continue;
    if (group && groupOf(row) !== group) continue;
    sum += -decorated.amountAgorot;
  }
  return sum;
}

function incomeIn(from: DayString, to: DayString): number {
  let sum = 0;
  for (const row of rowsIn(from, to)) {
    const decorated = decorate(row);
    if (decorated.scope === 'income' && decorated.amountAgorot !== null) sum += decorated.amountAgorot;
  }
  return sum;
}

function monthSpend(month: MonthString, group: GroupKey): number {
  const idx = MONTHS12.indexOf(month);
  if (idx >= 0) return a(CAT_MONTHLY[group][idx] ?? 0);
  if (month === CUR_MONTH) return a(SEP_MTD[group]);
  return 0;
}

function baselineOf(group: GroupKey, month: MonthString): { agorot: number; months: MonthString[]; n: number; partial: boolean; manual: boolean } {
  const override = state.categorySettings.get(group)?.manualBaselineAgorot;
  if (override) return { agorot: override, months: [], n: 0, partial: false, manual: true };
  const months = [addMonths(month, -3), addMonths(month, -2), addMonths(month, -1)].filter((m) => m >= '2025-09');
  if (!months.length) return { agorot: 0, months: [], n: 0, partial: true, manual: false };
  const total = months.reduce((sum, m) => sum + monthSpend(m, group), 0);
  return { agorot: Math.round(total / months.length), months, n: months.length, partial: months.length < 3, manual: false };
}

function thresholdPct(group: GroupKey): number {
  return state.categorySettings.get(group)?.thresholdPct ?? state.alertRules.thresholdPct;
}

function categoryStat(group: GroupKey, period: Period): CategoryStat {
  const amountAgorot = period.months.reduce((sum, m) => sum + monthSpend(m, group), 0);
  const monthAmountAgorot = monthSpend(period.lastMonth, group);
  const baseline = baselineOf(group, period.lastMonth);
  const pct = thresholdPct(group);
  const thresholdAgorot = Math.round(baseline.agorot * (1 + pct / 100));
  const ratio = baseline.agorot ? monthAmountAgorot / baseline.agorot : null;
  const over = baseline.agorot > 0 && monthAmountAgorot > thresholdAgorot;
  const severity = !over ? null : (ratio ?? 0) >= 1.5 || monthAmountAgorot - baseline.agorot >= 100000 ? 'critical' : 'warn';
  const day = Number(ASOF.slice(8, 10));
  const pace = period.isMonth && period.partial && day >= 10 ? Math.round(monthAmountAgorot / (day / 30)) : null;
  return {
    groupKey: group, amountAgorot, monthAmountAgorot, baseline, ratio, thresholdPct: pct, thresholdAgorot,
    over, severity, paceAgorot: pace,
    txCount: rowsIn(period.lastFrom, period.lastTo).filter((r) => decorate(r).scope === 'spend' && groupOf(r) === group).length,
    hidden: state.categorySettings.get(group)?.hidden ?? false,
  };
}

function sortedStats(period: Period): CategoryStat[] {
  return GROUP_KEYS.map((key) => categoryStat(key, period)).sort((x, y) => {
    if (x.over !== y.over) return x.over ? -1 : 1;
    if (x.over && y.over) return (y.ratio ?? 0) - (x.ratio ?? 0);
    return y.amountAgorot - x.amountAgorot;
  });
}

// ── cards (contract example payload, docs/api-personal.md §3.5) ──

function cycle(start: DayString, end: DayString, dueDate: DayString, status: CardCycle['status'], amount: number, installment: number, future: number, expected: number, txCount: number, matched?: { id: string; amount: number; date: DayString }): CardCycle {
  return {
    start, end, dueDate, status, amountAgorot: amount, installmentAgorot: installment,
    futureInstallmentAgorot: future, expectedAgorot: expected, txCount,
    matchedChargeTxId: matched?.id ?? null, matchedChargeAgorot: matched ? -matched.amount : null, matchedChargeDate: matched?.date ?? null,
  };
}

function maxCard(): CardSummary {
  const account = acc(ACC_MAX);
  const settings = state.cardSettings.get(ACC_MAX)!;
  return {
    account: accountRef(account), product: account.product, currency: 'ILS', creditStatus: 'enabled',
    creditLimitAgorot: account.creditLimitAgorot, usedAgorot: a(8310), utilization: 8310 / 14000,
    settings: { cutoffDay: settings.cutoffDay, dueDay: settings.dueDay, dueDaySource: 'derived', cycleBy: 'tx', chargedFromAccountId: ACC_CHK, verifiedCycles: 5, label: null, updatedAt: null },
    dueDayUnverified: false,
    current: cycle('2026-09-02', '2026-10-01', '2026-10-11', 'open', a(1470), 0, a(628.9), a(6903.33), 7),
    final: cycle('2026-08-02', '2026-09-01', '2026-09-10', 'final', a(6840), a(628.9), 0, a(6840), 41),
    variableForecastAgorot: a(6274.43),
    installmentsAvailable: false,
    installments: { plans: 0, monthlyAgorot: 0, lastDueMonth: null },
    lastCharged: cycle('2026-07-02', '2026-08-01', '2026-08-10', 'charged', a(7340), a(628.9), 0, a(7340), 44, { id: 'tx_charge_max_08', amount: a(7340), date: '2026-08-10' }),
    txCount: 828, chargedFromAssigned: true,
  };
}

function calCard(): CardSummary {
  const account = acc(ACC_CAL);
  const settings = state.cardSettings.get(ACC_CAL)!;
  return {
    account: accountRef(account), product: account.product, currency: 'ILS', creditStatus: 'enabled',
    creditLimitAgorot: account.creditLimitAgorot, usedAgorot: a(2390), utilization: 2390 / 100000,
    settings: { cutoffDay: settings.cutoffDay, dueDay: settings.dueDay, dueDaySource: 'financy', cycleBy: 'tx', chargedFromAccountId: ACC_CHK, verifiedCycles: 4, label: null, updatedAt: null },
    dueDayUnverified: false,
    current: cycle('2026-08-26', '2026-09-25', '2026-10-04', 'open', a(2390), 0, a(620), a(4393.33), 3),
    final: null,
    variableForecastAgorot: a(3773.33),
    installmentsAvailable: true,
    installments: { plans: 1, monthlyAgorot: a(620), lastDueMonth: '2027-01' },
    lastCharged: cycle('2026-07-26', '2026-08-25', '2026-09-02', 'charged', a(4120), a(620), 0, a(4120), 23, { id: 'tx_charge_cal_09', amount: a(4120), date: '2026-09-02' }),
    txCount: 350, chargedFromAssigned: true,
  };
}

function closedCard(id: string): CardSummary {
  const account = acc(id);
  return {
    account: accountRef(account), product: account.product, currency: 'ILS',
    creditStatus: account.creditStatus ?? 'deleted', creditLimitAgorot: account.creditLimitAgorot,
    usedAgorot: 0, utilization: account.creditLimitAgorot ? 0 : null,
    settings: { cutoffDay: 1, dueDay: 10, dueDaySource: 'derived', cycleBy: 'tx', chargedFromAccountId: null, verifiedCycles: 0, label: account.closedNote, updatedAt: null },
    dueDayUnverified: true, current: null, final: null, variableForecastAgorot: 0,
    installmentsAvailable: false, installments: { plans: 0, monthlyAgorot: 0, lastDueMonth: null },
    lastCharged: null, txCount: account.txCount, chargedFromAssigned: null,
  };
}

function coverage(): Coverage {
  const cards = [maxCard(), calCard()];
  const items = cards.flatMap((card) => {
    const out = [];
    for (const c of [card.final, card.current]) {
      if (!c || c.status === 'charged') continue;
      const daysUntil = Math.round((Date.parse(c.dueDate) - Date.parse(ASOF)) / 86400000);
      out.push({
        accountId: card.account.id, providerId: card.account.providerId, name: card.account.name,
        numberLast4: card.account.numberLast4,
        cycle: { start: c.start, end: c.end, status: c.status },
        dueDate: c.dueDate, daysUntil, knownAgorot: c.amountAgorot, expectedAgorot: c.expectedAgorot,
        inWindow: daysUntil <= 30, chargedFromAccountId: ACC_CHK, chargedFromAssigned: true,
        connectionStatus: card.account.connectionStatus, dataAsOf: card.account.dataAsOf,
      });
    }
    return out;
  }).sort((x, y) => (x.dueDate < y.dueDate ? -1 : 1));

  const inWindow = items.filter((i) => i.inWindow);
  const available = a(18420);
  const known = inWindow.reduce((s, i) => s + i.knownAgorot, 0);
  const expected = inWindow.reduce((s, i) => s + Math.max(i.knownAgorot, i.expectedAgorot), 0);
  return {
    window: { from: ASOF, to: addDays(ASOF, 30) },
    items,
    accounts: [{
      accountId: ACC_CHK, numberLast4: '6516', assigned: true, availableAgorot: available,
      availableSource: 'forwardAvailable', bookedInclLimitAgorot: a(28420),
      chargesKnownAgorot: known, chargesExpectedAgorot: expected,
      coverageKnownAgorot: available - known, coverageExpectedAgorot: available - expected,
    }],
    overall: {
      availableAgorot: available, chargesKnownAgorot: known, chargesExpectedAgorot: expected,
      coverageKnownAgorot: available - known, coverageExpectedAgorot: available - expected,
      ok: available - known >= 0, okExpected: available - expected >= 0,
    },
    excluded: [], computable: true,
  };
}

function schedule(cardIds?: string[]): Schedule {
  const cards = [maxCard(), calCard()].filter((c) => !cardIds || cardIds.includes(c.account.id));
  const months = trailingMonths(addMonths(CUR_MONTH, 6), 6);
  const known: Record<string, number[]> = {
    [ACC_MAX]: [a(628.9), a(628.9), a(628.9), a(628.9), a(628.9), a(349.9)],
    [ACC_CAL]: [a(620), a(620), a(620), a(620), 0, 0],
  };
  const rows = months.map((month, i) => {
    const byCard: Record<string, { knownAgorot: number; forecastAgorot: number }> = {};
    let k = 0;
    let f = 0;
    for (const card of cards) {
      const cardKnown = known[card.account.id]?.[i] ?? 0;
      byCard[card.account.id] = { knownAgorot: cardKnown, forecastAgorot: card.variableForecastAgorot };
      k += cardKnown;
      f += card.variableForecastAgorot;
    }
    return { month, knownAgorot: k, forecastAgorot: f, totalAgorot: k + f, byCard };
  });
  return {
    months: rows,
    totals: {
      knownAgorot: rows.reduce((s, r) => s + r.knownAgorot, 0),
      forecastAgorot: rows.reduce((s, r) => s + r.forecastAgorot, 0),
      totalAgorot: rows.reduce((s, r) => s + r.totalAgorot, 0),
    },
    cards: cards.map((c) => ({ accountId: c.account.id, variableForecastAgorot: c.variableForecastAgorot })),
  };
}

// ── net worth ──

const MANUAL_SECURITIES: ManualEntry = {
  id: 'man_sct', kind: 'balance', side: 'asset', label: 'תיק ני"ע (ידני)',
  balanceAgorot: a(112350), asOf: '2026-09-05', note: 'עד שהחיבור לני"ע יעבוד',
  createdBy: 'יאיר', createdAt: '2026-09-01T10:00:00.000Z',
};
const manualEntries: ManualEntry[] = [MANUAL_SECURITIES];

function netWorthPoints(): NetWorthPoint[] {
  const points: NetWorthPoint[] = [];
  const months = [...MONTHS12, CUR_MONTH];
  months.forEach((month, i) => {
    const assets = a((BAL.chk[i] ?? 0) + (BAL.sav[i] ?? 0) + (BAL.sct[i] ?? 0));
    const liabilities = a((BAL.loan[i] ?? 0) + (BAL.max[i] ?? 0) + (BAL.cal[i] ?? 0) + (BAL.crd[i] ?? 0));
    const isCurrent = i === months.length - 1;
    points.push({
      month, day: isCurrent ? ASOF : monthEnd(month),
      netAgorot: assets - liabilities, assetsAgorot: assets, liabilitiesAgorot: liabilities,
      estimated: !isCurrent, isCurrent,
    });
  });
  return points;
}

function netWorth(): NetWorthResponse {
  const points = netWorthPoints();
  const current = points[points.length - 1]!;
  const prev = points[points.length - 2]!;
  const first = points[0]!;
  const monthChange = current.netAgorot - prev.netAgorot;
  const yearChange = current.netAgorot - first.netAgorot;

  const change = (key: keyof typeof BAL): number => a((BAL[key][12] ?? 0) - (BAL[key][11] ?? 0));

  const assets: NetWorthAccount[] = [
    nwRow(acc(ACC_CHK), 'checking', a(18420), current.assetsAgorot, change('chk')),
    nwRow(acc(ACC_SAV), 'savings', a(64000), current.assetsAgorot, change('sav')),
    {
      accountId: null, manualEntryId: MANUAL_SECURITIES.id, group: 'manual_asset', type: 'MANUAL',
      providerId: null, name: MANUAL_SECURITIES.kind === 'balance' ? MANUAL_SECURITIES.label : 'תיק ני"ע',
      numberLast4: null, balanceAgorot: a(112350), share: a(112350) / current.assetsAgorot,
      monthChangeAgorot: change('sct'), interest: null, note: null, connectionStatus: null, dataAsOf: null,
      creditStatus: null, loanType: null, contractEnd: null, card: null, issuerLoanAccountIds: [],
    },
    {
      accountId: null, manualEntryId: null, group: 'securities', type: 'SECURITY', providerId: 'discount',
      name: 'ניירות ערך', numberLast4: null, balanceAgorot: 0, share: 0, monthChangeAgorot: null,
      interest: null, note: null, connectionStatus: null, dataAsOf: null, creditStatus: null,
      loanType: null, contractEnd: null, card: null, issuerLoanAccountIds: [],
    },
  ];

  // Every liability the server would build from the SAME account fixtures the loans screen reads:
  // the bank loan, the open issuer facilities (`issuer_credit`, BRIEF §9.4) and the two cards. The
  // business loan (`assigned: false`) is correctly absent — personal net worth is assigned-only —
  // and the loans screen's `סך החוב` is larger for exactly that reason and no other.
  const facilities = accountsData.filter(
    (x) => x.type === 'LOAN' && x.loanType === 'CREDIT_CARD' && x.assigned && (x.contractEnd === null || x.contractEnd >= ASOF),
  );
  const liabilities: NetWorthAccount[] = [
    nwRow(acc(ACC_LOAN), 'bank_loan', a(38600), current.liabilitiesAgorot, change('loan')),
    ...facilities.map((facility) => ({
      ...nwRow(facility, 'issuer_credit', Math.abs(facility.balances.booked ?? 0), current.liabilitiesAgorot, 0),
      loanType: facility.loanType,
      contractEnd: facility.contractEnd,
    })),
    {
      ...nwRow(acc(ACC_MAX), 'card', a(8310), current.liabilitiesAgorot, change('max')),
      card: { finalAgorot: a(6840), finalDueDate: '2026-09-10', accruedAgorot: a(1470), accruedFrom: '2026-09-02' },
      issuerLoanAccountIds: facilities.filter((x) => x.providerId === 'max').map((x) => x.id),
    },
    {
      ...nwRow(acc(ACC_CAL), 'card', a(2390), current.liabilitiesAgorot, change('cal')),
      card: { finalAgorot: null, finalDueDate: null, accruedAgorot: a(2390), accruedFrom: '2026-08-26' },
      issuerLoanAccountIds: facilities.filter((x) => x.providerId === 'cal').map((x) => x.id),
    },
  ];

  return {
    asOf: ASOF, netAgorot: current.netAgorot, assetsAgorot: current.assetsAgorot, liabilitiesAgorot: current.liabilitiesAgorot,
    monthChangeAgorot: monthChange, monthChangePct: Math.round((monthChange / prev.netAgorot) * 1000) / 10,
    yearChangeAgorot: yearChange, yearChangePct: Math.round((yearChange / first.netAgorot) * 1000) / 10,
    history: points, historyFrom: '2025-09-30', assets, liabilities, securities: { status: 'not_connected' },
  };
}

function nwRow(account: MockAccount, group: NetWorthAccount['group'], balance: number, sideTotal: number, monthChange: number): NetWorthAccount {
  return {
    accountId: account.id, manualEntryId: null, group, type: account.type, providerId: account.providerId,
    name: account.name, numberLast4: account.numberLast4, balanceAgorot: balance,
    share: sideTotal ? balance / sideTotal : 0, monthChangeAgorot: monthChange, interest: account.interest,
    note: account.note, connectionStatus: account.connectionStatus, dataAsOf: account.dataAsOf,
    creditStatus: account.creditStatus, loanType: account.loanType, contractEnd: account.contractEnd,
    card: null, issuerLoanAccountIds: [],
  };
}

// -- loans (REDESIGN 6) --

/** Whole months from `from` to `to`, floored, never negative. */
function wholeMonths(from: DayString, to: DayString): number {
  const months = monthsBetween(from, to).length - 1;
  const partial = Number(to.slice(8, 10)) < Number(from.slice(8, 10));
  return Math.max(0, partial ? months - 1 : months);
}

/** `interest` as the contract shapes it, from the single `InterestSummary` the mock carries. */
function loanInterest(account: MockAccount): LoanInterest {
  const rate = account.interest?.ratePct ?? null;
  const indexed = account.interest?.type === 'INDE';
  return {
    ratePct: rate,
    structure: rate === null ? 'unknown' : indexed ? 'prime' : 'fixed',
    fixedPct: indexed ? null : rate,
    indexPct: indexed ? rate : null,
    indexApplied: false,
    indexName: account.interest?.relatedIndex ?? null,
    tiered: false,
    tiers:
      rate === null
        ? []
        : [
            {
              kind: account.interest?.type ?? null,
              ratePct: rate,
              fromAgorot: null,
              toAgorot: null,
              indexed,
              indexName: account.interest?.relatedIndex ?? null,
              toDate: null,
              applicable: true,
            },
          ],
  };
}

/** A 12-step amortization of the next year's interest -- the shape the server reports. */
function annualInterest(balance: number, ratePct: number | null, payment: number | null): number | null {
  if (ratePct === null) return null;
  if (ratePct <= 0 || balance <= 0) return 0;
  const r = ratePct / 100 / 12;
  if (payment === null || payment <= 0) return Math.round(balance * (ratePct / 100));
  let left = balance;
  let total = 0;
  for (let i = 0; i < 12 && left > 0; i += 1) {
    const interest = left * r;
    const principal = payment - interest;
    if (principal <= 0) return Math.round(balance * (ratePct / 100));
    total += interest;
    left = Math.max(0, left - principal);
  }
  return Math.round(total);
}

/** The next occurrence of a contract's day of month, on or after `asOf`. */
function nextPaymentDay(anchorDay: DayString): DayString {
  const day = Math.min(28, Number(anchorDay.slice(8, 10)) || 1);
  const thisMonth = `${CUR_MONTH}-${String(day).padStart(2, '0')}`;
  if (thisMonth >= ASOF) return thisMonth;
  return `${addMonths(CUR_MONTH, 1)}-${String(day).padStart(2, '0')}`;
}

function loanRow(account: MockAccount): LoanRow {
  const balance = Math.abs(account.balances.booked ?? 0);
  const endDate = account.note?.maturityDate ?? account.contractEnd;
  const endsInMonths = endDate === null ? null : wholeMonths(ASOF, endDate);
  const noted = account.note?.monthlyPaymentAgorot ?? null;
  const months = account.note?.remainingPayments ?? endsInMonths;
  const rate = account.interest?.ratePct ?? null;
  let payment = noted;
  if (payment === null && months !== null && months > 0 && balance > 0) {
    const r = (rate ?? 0) / 100 / 12;
    payment = r > 0 ? Math.round((balance * r) / (1 - Math.pow(1 + r, -months))) : Math.round(balance / months);
  }
  const card =
    account.loanType === 'CREDIT_CARD'
      ? (accountsData.find((x) => x.type === 'CARD' && x.providerId === account.providerId && x.numberLast4 === account.numberLast4) ?? null)
      : null;
  const status: LoanRow['status'] = endDate !== null && endDate < ASOF ? 'closed' : 'open';
  return {
    accountId: account.id,
    providerId: account.providerId,
    connectionId: account.connectionId,
    name: account.name,
    product: account.product,
    numberLast4: account.numberLast4,
    kind: account.loanType === 'CREDIT_CARD' ? 'card-credit' : 'bank',
    loanType: account.loanType,
    status,
    assignment: account.assigned ? 'personal' : 'business',
    balanceAgorot: balance,
    creditLimitAgorot: account.creditLimitAgorot,
    ratePct: rate,
    interest: loanInterest(account),
    monthlyPaymentAgorot: status === 'closed' ? null : payment,
    monthlyPaymentEstimated: noted === null && payment !== null,
    monthlyPaymentSource: noted !== null ? 'note' : payment !== null ? 'amortized' : 'none',
    startDate: account.contractStart,
    endDate,
    endsInMonths,
    estimatedAnnualInterestAgorot: status === 'closed' ? null : annualInterest(balance, rate, payment),
    // The contract's own day of month, projected forward from `asOf` -- the shape the server sends.
    nextPaymentDate: status === 'closed' || endDate === null ? null : nextPaymentDay(account.contractStart ?? endDate),
    card: card
      ? { accountId: card.id, providerId: card.providerId, name: card.name, numberLast4: card.numberLast4, creditStatus: card.creditStatus }
      : null,
    note: account.note,
    connectionStatus: account.connectionStatus,
    dataAsOf: account.dataAsOf,
    txCount: 0,
  };
}

/** Every LOAN account that is a loan -- `CREDIT_LIMIT` (the overdraft facility) never is. */
function allLoanRows(): LoanRow[] {
  return accountsData.filter((x) => x.type === 'LOAN' && x.loanType !== 'CREDIT_LIMIT').map(loanRow);
}

function loanTotals(rows: readonly LoanRow[]): LoansTotals {
  const open = rows.filter((row) => row.status === 'open');
  let debt = 0;
  let payment = 0;
  let estimated = 0;
  let interest = 0;
  let weight = 0;
  let rated = 0;
  for (const row of open) {
    debt += row.balanceAgorot;
    if (row.monthlyPaymentAgorot !== null) {
      payment += row.monthlyPaymentAgorot;
      if (row.monthlyPaymentEstimated) estimated += row.monthlyPaymentAgorot;
    }
    interest += row.estimatedAnnualInterestAgorot ?? 0;
    if (row.ratePct !== null) {
      weight += row.ratePct * row.balanceAgorot;
      rated += row.balanceAgorot;
    }
  }
  return {
    count: rows.length,
    openCount: open.length,
    debtAgorot: debt,
    monthlyPaymentAgorot: payment,
    monthlyPaymentEstimatedAgorot: estimated,
    weightedRatePct: rated > 0 ? Math.round((weight / rated) * 100) / 100 : null,
    estimatedAnnualInterestAgorot: interest,
  };
}

function loanTrendPoints(debtNow: number): LoanTrendPoint[] {
  const base = BAL.loan[12] ?? 1;
  return netWorthPoints().map((point, index) => ({
    month: point.month,
    day: point.day,
    debtAgorot: Math.round((debtNow * (BAL.loan[index] ?? base)) / base),
    estimated: !point.isCurrent,
    isCurrent: point.isCurrent,
  }));
}

function sortLoanRows(rows: readonly LoanRow[], sort: LoanSort): LoanRow[] {
  const desc = (x: number | null, y: number | null) => (x === null ? 1 : y === null ? -1 : y - x);
  const byEnd = (x: LoanRow, y: LoanRow) =>
    x.endDate === null ? 1 : y.endDate === null ? -1 : x.endDate < y.endDate ? -1 : x.endDate > y.endDate ? 1 : 0;
  const out = [...rows];
  out.sort((x, y) => {
    const first =
      sort === 'rate'
        ? desc(x.ratePct, y.ratePct)
        : sort === 'balance'
          ? desc(x.balanceAgorot, y.balanceAgorot)
          : sort === 'payment'
            ? desc(x.monthlyPaymentAgorot, y.monthlyPaymentAgorot)
            : byEnd(x, y);
    return first || y.balanceAgorot - x.balanceAgorot || (x.accountId < y.accountId ? -1 : 1);
  });
  return out;
}

function loanCardGroups(rows: readonly LoanRow[]): LoanCardGroup[] {
  const groups = new Map<string, LoanCardGroup>();
  for (const row of rows) {
    if (row.kind !== 'card-credit') continue;
    const key = row.card?.accountId ?? `${row.providerId}:${row.numberLast4 ?? ''}`;
    const found = groups.get(key);
    if (found) {
      found.count += 1;
      found.balanceAgorot += row.balanceAgorot;
      found.monthlyPaymentAgorot += row.monthlyPaymentAgorot ?? 0;
      found.accountIds.push(row.accountId);
      continue;
    }
    groups.set(key, {
      accountId: row.card?.accountId ?? null,
      providerId: row.providerId,
      name: row.card?.name ?? row.name,
      numberLast4: row.card?.numberLast4 ?? row.numberLast4,
      creditStatus: row.card?.creditStatus ?? null,
      count: 1,
      balanceAgorot: row.balanceAgorot,
      monthlyPaymentAgorot: row.monthlyPaymentAgorot ?? 0,
      accountIds: [row.accountId],
    });
  }
  return [...groups.values()];
}

const ENDING_HORIZON_MONTHS = 12;

function loansEndingSoon(rows: readonly LoanRow[]): LoansResponse['endingSoon'] {
  const soon = rows.filter(
    (row) => row.status === 'open' && row.endDate !== null && row.endsInMonths !== null && row.endsInMonths <= ENDING_HORIZON_MONTHS,
  );
  const dates = soon.map((row) => row.endDate ?? '').filter((day) => day !== '').sort();
  const lastDate = dates[dates.length - 1];
  return {
    horizonMonths: ENDING_HORIZON_MONTHS,
    count: soon.length,
    monthlyFreedAgorot: soon.reduce((sum, row) => sum + (row.monthlyPaymentAgorot ?? 0), 0),
    nextEndDate: dates[0] ?? null,
    lastEndDate: lastDate ?? null,
    withinMonths: lastDate === undefined ? null : wholeMonths(ASOF, lastDate),
    accountIds: soon.map((row) => row.accountId),
  };
}

/**
 * REDESIGN 2/3 -- the demo server's side of `docs/insight-contract.md`: it supplies FACTS and
 * one-line Hebrew titles, and `shared/insight` does the bucketing, the ordering, the countdown
 * chips and every sentence. The web app renders whatever comes back, exactly as it does against
 * the real server.
 */
type SummaryBase = Omit<SummaryResponse, 'narrative' | 'urgency' | 'loans'>;

const GROUP_LABEL: Readonly<Record<string, string>> = {
  HOUSING: '\u05d3\u05d9\u05d5\u05e8 \u05d5\u05d7\u05e9\u05d1\u05d5\u05e0\u05d5\u05ea',
  GROCERIES: '\u05de\u05d6\u05d5\u05df \u05d5\u05e1\u05d5\u05e4\u05e8',
  KIDS: '\u05d9\u05dc\u05d3\u05d9\u05dd \u05d5\u05d7\u05d9\u05e0\u05d5\u05da',
  TRANSPORT: '\u05ea\u05d7\u05d1\u05d5\u05e8\u05d4 \u05d5\u05e8\u05db\u05d1',
  DINING: '\u05de\u05e1\u05e2\u05d3\u05d5\u05ea \u05d5\u05d1\u05d9\u05dc\u05d5\u05d9',
  SHOPPING: '\u05e7\u05e0\u05d9\u05d5\u05ea \u05d5\u05d1\u05d9\u05d2\u05d5\u05d3',
  INSURANCE: '\u05d1\u05d9\u05d8\u05d5\u05d7',
  HEALTH: '\u05d1\u05e8\u05d9\u05d0\u05d5\u05ea',
  TELECOM: '\u05ea\u05e7\u05e9\u05d5\u05e8\u05ea \u05d5\u05de\u05e0\u05d5\u05d9\u05d9\u05dd',
  OTHER: '\u05d0\u05d7\u05e8',
};

function shekelText(agorot: number): string {
  return `\u20aa${Math.round(Math.abs(agorot) / 100).toLocaleString('en-US')}`;
}

function urgencyInputsFor(base: SummaryBase, loans: LoansResponse, returnRows: readonly TransactionRow[]): UrgencyInput[] {
  const inputs: UrgencyInput[] = [];
  const overall = base.coverage.overall;

  for (const item of base.coverage.items) {
    inputs.push({
      id: `card_charge:${item.accountId}:${item.dueDate}`,
      kind: 'card_charge',
      date: item.dueDate,
      title: `\u05d7\u05d9\u05d5\u05d1 ${item.name}${item.numberLast4 ? ` ****${item.numberLast4}` : ''}`,
      amountAgorot: -Math.abs(item.knownAgorot),
      providerId: item.providerId,
      actionHref: `/cards/${item.accountId}`,
      severity: overall && !overall.ok ? 'warn' : 'info',
    });
  }

  if (overall && !overall.ok) {
    const first = base.coverage.items.find((item) => item.inWindow) ?? base.coverage.items[0];
    inputs.push({
      id: 'coverage_shortfall',
      kind: 'coverage_shortfall',
      date: first ? first.dueDate : null,
      title: `\u05d7\u05e1\u05e8\u05d9\u05dd ${shekelText(overall.coverageKnownAgorot)} \u05dc\u05db\u05d9\u05e1\u05d5\u05d9 \u05d7\u05d9\u05d5\u05d1\u05d9 \u05d4\u05d0\u05e9\u05e8\u05d0\u05d9`,
      amountAgorot: overall.coverageKnownAgorot,
      actionHref: '/cards',
    });
  }

  // One item PER ROW, with the row's own date and the server's titles (personal/server/payloads.ts).
  // The aggregate version carried `date: null`, so the two rows that fill `עכשיו` in the preview
  // rendered with no date and no countdown chip — the one thing REDESIGN §1/§2/§10 requires of every home
  // item — and it printed the fee amount in the title right beside the amount column.
  for (const row of returnRows) {
    if (row.returnKind === null || row.amountAgorot === null) continue;
    const principal = row.returnKind === 'principal';
    inputs.push({
      id: `${principal ? 'returned_payment' : 'return_fee'}:${row.id}`,
      kind: principal ? 'returned_payment' : 'return_fee',
      date: row.effDate,
      title: `${principal ? 'הוראת קבע או שיק חזרו' : 'עמלת החזרה'}: ${row.merchant ?? row.description}`,
      amountAgorot: row.amountAgorot,
      actionHref: `/transactions/${row.id}`,
    });
  }

  for (const stat of base.categories) {
    if (!stat.over) continue;
    inputs.push({
      id: `category_over_baseline:${stat.groupKey}`,
      kind: 'category_over_baseline',
      date: null,
      title: `${GROUP_LABEL[stat.groupKey] ?? stat.groupKey} ${shekelText(stat.monthAmountAgorot)} \u05de\u05d5\u05dc \u05d1\u05e1\u05d9\u05e1 ${shekelText(stat.baseline.agorot)}`,
      amountAgorot: -Math.abs(stat.monthAmountAgorot),
      actionHref: `/categories/${stat.groupKey}`,
      severity: stat.severity ?? 'warn',
    });
  }

  for (const loan of loans.items) {
    if (loan.assignment !== 'personal' || loan.nextPaymentDate === null || loan.monthlyPaymentAgorot === null) continue;
    inputs.push({
      id: `loan_payment:${loan.accountId}`,
      kind: 'loan_payment',
      date: loan.nextPaymentDate,
      title: `\u05ea\u05e9\u05dc\u05d5\u05dd \u05d4\u05dc\u05d5\u05d5\u05d0\u05d4 \u2014 ${loan.name}`,
      amountAgorot: -Math.abs(loan.monthlyPaymentAgorot),
      providerId: loan.providerId,
      actionHref: '/loans',
    });
  }

  for (const connection of base.connections) {
    if (!connection.needsRelink) continue;
    inputs.push({
      id: `connection_expired:${connection.id}`,
      kind: 'connection_expired',
      date: connection.expiryDate,
      title: `\u05d4\u05d7\u05d9\u05d1\u05d5\u05e8 \u05dc-${connection.providerId === 'cal' ? 'Cal' : connection.providerId} \u05e4\u05d2 \u05ea\u05d5\u05e7\u05e3`,
      amountAgorot: null,
      providerId: connection.providerId,
      actionHref: '/settings/connections',
      // The data is still fresh, so the lapsed consent waits in `\u05d0\u05e4\u05e9\u05e8 \u05dc\u05d7\u05db\u05d5\u05ea` (REDESIGN 8).
      pin: base.freshness.level === 'fresh' ? 'later' : 'now',
    });
  }

  return inputs;
}

function narrativeInputFor(base: SummaryBase, items: ReturnType<typeof buildItems>): PersonalNarrativeInput {
  const overall = base.coverage.overall;
  const deviation = base.categories.find((stat) => stat.over && stat.ratio !== null);
  // Same rule as the server: only a DEADLINE kind can be "the closest thing" (a returned payment
  // dated today already happened).
  const next = items.find((item) => item.date !== null && item.daysUntil !== null && item.daysUntil >= 0 && URGENCY_KIND_SPECS[item.kind].deadline);
  return {
    system: 'personal',
    month: { inAgorot: base.cashflow.bank.inAgorot, outAgorot: base.cashflow.bank.outAgorot, netAgorot: base.cashflow.bank.netAgorot },
    coverage:
      overall === null
        ? null
        : {
            chargesAgorot: Math.abs(overall.chargesKnownAgorot),
            availableAgorot: overall.availableAgorot,
            shortfall: overall.ok
              ? null
              : {
                  gapAgorot: -Math.abs(overall.coverageKnownAgorot),
                  date: base.coverage.items[0]?.dueDate ?? ASOF,
                },
            leftAgorot: overall.coverageKnownAgorot,
          },
    deviation:
      deviation === undefined
        ? null
        : {
            label: GROUP_LABEL[deviation.groupKey] ?? deviation.groupKey,
            amountAgorot: Math.abs(deviation.monthAmountAgorot),
            baselineAgorot: Math.abs(deviation.baseline.agorot),
            ratio: deviation.ratio ?? 1,
          },
    returns: { count: base.cashflow.returnsThisMonth.count, feesAgorot: base.cashflow.returnsThisMonth.feesAgorot },
    next: next === undefined || next.date === null ? null : { label: next.title, date: next.date },
    quiet: null,
  };
}

function loansResponse(sort: LoanSort = 'rate'): LoansResponse {
  const rows = allLoanRows();
  const open = rows.filter((row) => row.status === 'open');
  const closed = rows.filter((row) => row.status === 'closed');
  const totals = loanTotals(rows);
  const points = loanTrendPoints(totals.debtAgorot);
  const current = points[points.length - 1];
  const prev = points[points.length - 2];
  const firstPoint = points[0];
  return {
    asOf: ASOF,
    sort,
    totals,
    byAssignment: {
      personal: loanTotals(rows.filter((row) => row.assignment === 'personal')),
      business: loanTotals(rows.filter((row) => row.assignment === 'business')),
    },
    byKind: {
      bank: loanTotals(rows.filter((row) => row.kind === 'bank')),
      'card-credit': loanTotals(rows.filter((row) => row.kind === 'card-credit')),
    },
    trend: {
      points,
      changeAgorot: (current?.debtAgorot ?? 0) - (prev?.debtAgorot ?? 0),
      avgMonthlyChangeAgorot:
        points.length >= 2 ? Math.round(((current?.debtAgorot ?? 0) - (firstPoint?.debtAgorot ?? 0)) / (points.length - 1)) : null,
      historyFrom: '2025-09-30',
    },
    items: sortLoanRows(open, sort),
    closed: sortLoanRows(closed, 'balance'),
    endingSoon: loansEndingSoon(rows),
    cards: loanCardGroups(open),
  };
}

// ── alerts (spec §7.8 + BRIEF §9.3) ──

const alertsData: Alert[] = [
  { id: 'al_1', type: 'connection_expired', severity: 'critical', title: 'החיבור ל-Cal פג תוקף', body: 'הנתונים מ-Cal מוצגים נכון ל-06.09.2026 06:09. פתח את אפליקציית Financy וחבר מחדש.', entityType: 'connection', entityId: CONN_CAL, amountAgorot: null, createdAt: '2026-09-06T03:15:00.000Z', readAt: null, dismissedAt: null, resolved: false },
  { id: 'al_2', type: 'returned_payment', severity: 'warn', title: 'הוראת קבע חזרה: ביטוח ישיר ₪340 · עמלה ₪38', body: 'ב-06.09.2026 · עו"ש ***6516. הבנק החזיר את החיוב וגבה עמלה של ₪38. בדוק את ההרשאה לחיוב חשבון או שלם ישירות.', entityType: 'transaction', entityId: 'ret_1', amountAgorot: a(340), createdAt: '2026-09-06T03:15:00.000Z', readAt: null, dismissedAt: null, resolved: false },
  { id: 'al_3', type: 'category_over_baseline', severity: 'critical', title: 'קניות וביגוד: ₪2,210 — ×1.9 מהבסיס', body: 'הבסיס (יוני–אוגוסט) הוא ₪1,150. הסף: 20%.', entityType: 'category', entityId: 'SHOPPING', amountAgorot: a(2210), createdAt: '2026-09-05T03:15:00.000Z', readAt: null, dismissedAt: null, resolved: false },
  { id: 'al_4', type: 'large_transaction', severity: 'warn', title: 'הוצאה גדולה: איקאה ₪1,890', body: '04.09.2026 · Cal ****2391 · קניות וביגוד', entityType: 'transaction', entityId: 'ikea_1', amountAgorot: a(1890), createdAt: '2026-09-05T03:15:00.000Z', readAt: null, dismissedAt: null, resolved: false },
  { id: 'al_5', type: 'card_due_soon', severity: 'info', title: 'חיוב Max ****6044 ב-10.09 — ₪6,840', body: 'בעוד 4 ימים. זמין בעו"ש: ₪18,420 · יש כיסוי', entityType: 'card', entityId: ACC_MAX, amountAgorot: a(6840), createdAt: '2026-09-05T03:15:00.000Z', readAt: null, dismissedAt: null, resolved: false },
  { id: 'al_6', type: 'category_over_baseline', severity: 'critical', title: 'מסעדות ובילוי: ₪1,980 — ×1.7 מהבסיס', body: 'הבסיס (מאי–יולי) הוא ₪1,177. הסף: 20%.', entityType: 'category', entityId: 'DINING', amountAgorot: a(1980), createdAt: '2026-08-24T03:15:00.000Z', readAt: '2026-08-24T06:00:00.000Z', dismissedAt: null, resolved: false },
  { id: 'al_7', type: 'category_over_baseline', severity: 'critical', title: 'אחר: ₪1,560 — ×1.6 מהבסיס', body: 'הבסיס (מאי–יולי) הוא ₪973. הסף: 20%.', entityType: 'category', entityId: 'OTHER', amountAgorot: a(1560), createdAt: '2026-08-19T03:15:00.000Z', readAt: '2026-08-19T07:00:00.000Z', dismissedAt: null, resolved: false },
  { id: 'al_8', type: 'new_account', severity: 'info', title: 'חשבון חדש מ-Financy: עו"ש ***6751', body: 'החשבון לא משויך למערכת זו. שייך אותו בהגדרות → חשבונות וכרטיסים אם הוא שייך למשק הבית.', entityType: 'account', entityId: ACC_CHK_BIZ, amountAgorot: null, createdAt: '2026-07-27T03:15:00.000Z', readAt: '2026-07-27T09:00:00.000Z', dismissedAt: null, resolved: false },
];

function alertView(alert: Alert): Alert {
  const patch = state.alertState.get(alert.id);
  return patch ? { ...alert, readAt: patch.readAt, dismissedAt: patch.dismissedAt } : alert;
}

function openAlerts(): Alert[] {
  return alertsData.map(alertView).filter((al) => !al.dismissedAt);
}

function unreadAlerts(): Alert[] {
  return openAlerts().filter((al) => !al.readAt);
}

// ───────────────────────────── the adapter ─────────────────────────────

const SEVERITY_RANK: Record<string, number> = { critical: 0, warn: 1, info: 2 };

function delay<T>(value: T, ms = 120): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

export function createMockApi(): Api {
  const listeners = new Set<(event: import('../../shared-types.ts').SseEvent) => void>();
  const emit = (event: import('../../shared-types.ts').SseEvent) => {
    for (const listener of listeners) listener(event);
  };

  function freshness(): SummaryResponse['freshness'] {
    return { lastFetchedAt: FETCHED_AT, ageHours: 3.05, level: 'fresh', staleHours: state.alertRules.staleHours, criticalHours: 72, asOf: ASOF };
  }

  function summary(periodParam?: PeriodParam): SummaryResponse {
    const period = resolvePeriod(periodParam);
    const cov = coverage();
    const points = netWorthPoints();
    const current = points[points.length - 1]!;
    const prev = points[points.length - 2]!;
    const spendOut = spendIn(period.from, period.to);
    const spendIncome = incomeIn(period.from, period.to);
    const prevOut = period.prev ? spendIn(period.prev.from, period.prev.to) : null;
    const prevIn = period.prev ? incomeIn(period.prev.from, period.prev.to) : null;
    const cardCharge = rowsIn(period.from, period.to)
      .filter((r) => r.scope === 'card-charge')
      .reduce((s, r) => s + Math.abs(r.amountAgorot ?? 0), 0);
    const checkingSpend = rowsIn(period.from, period.to)
      .filter((r) => r.kind === 'CHECKING' && decorate(r).scope === 'spend')
      .reduce((s, r) => s + Math.abs(r.amountAgorot ?? 0), 0);
    const feeReturn = rowsIn(period.from, period.to)
      .filter((r) => r.returnKind === 'fee')
      .reduce((s, r) => s + Math.abs(r.amountAgorot ?? 0), 0);
    const bankOut = checkingSpend + cardCharge;
    const returnsMonth = rowsIn(monthStart(CUR_MONTH), ASOF).filter((r) => r.returnKind === 'principal');
    const day = Number(period.to.slice(8, 10));
    const dim = daysInMonth(Number(period.lastMonth.slice(0, 4)), Number(period.lastMonth.slice(5, 7)));

    const base: SummaryBase = {
      asOf: ASOF, serverTime: SERVER_TIME, period, state: 'ok', freshness: freshness(),
      connections: connectionsData.map((c) => ({ id: c.id, providerId: c.providerId, status: c.status, lastFetchedAt: c.lastFetchedAt, expiryDate: c.expiryDate, maskedPans: c.maskedPans, needsRelink: c.needsRelink })),
      credits: { usedThisMonth: state.creditsUsed, plan: { tier: 'starter', credits: 500 }, perRefresh: 20 },
      cashflow: {
        bank: { inAgorot: spendIncome, outAgorot: bankOut, netAgorot: spendIncome - bankOut, txCount: 9, outBreakdown: { spendAgorot: checkingSpend, cardChargeAgorot: cardCharge, taxAgorot: 0, savingsAgorot: 0, feeReturnAgorot: feeReturn } },
        bankPrev: prevIn === null ? null : { inAgorot: prevIn, outAgorot: a(15900), netAgorot: prevIn - a(15900), txCount: 11, outBreakdown: { spendAgorot: a(9800), cardChargeAgorot: a(6100), taxAgorot: 0, savingsAgorot: 0, feeReturnAgorot: 0 } },
        spending: { inAgorot: spendIncome, outAgorot: spendOut, netAgorot: spendIncome - spendOut, txCount: rowsIn(period.from, period.to).filter((r) => ['spend', 'income'].includes(decorate(r).scope)).length },
        spendingPrev: prevOut === null || prevIn === null ? null : { inAgorot: prevIn, outAgorot: prevOut, netAgorot: prevIn - prevOut, txCount: 19 },
        progress: period.isMonth && period.partial
          ? { day, daysInMonth: dim, remaining: dim - day, ratio: day / dim, nextCharge: { dueDate: '2026-09-10', accountId: ACC_MAX, knownAgorot: a(6840) } }
          : null,
        expectedIncomes: period.isMonth && period.partial
          ? [{ merchant: 'LabTop — משיכת בעלים', merchantKey: 'labtop', day: 10, amountAgorot: a(9600), occurrences: 3, expectedDate: '2026-09-10' }]
          : [],
        returnsThisMonth: { count: returnsMonth.length, feesAgorot: a(38) },
      },
      categories: sortedStats(period).filter((s) => !s.hidden),
      coverage: cov,
      netWorth: {
        netAgorot: current.netAgorot, assetsAgorot: current.assetsAgorot, liabilitiesAgorot: current.liabilitiesAgorot,
        monthChangeAgorot: current.netAgorot - prev.netAgorot,
        monthChangePct: Math.round(((current.netAgorot - prev.netAgorot) / prev.netAgorot) * 1000) / 10,
        sparkline: points, securities: { status: 'not_connected' },
      },
      alerts: {
        unreadCount: unreadAlerts().length,
        unread: unreadAlerts().sort((x, y) => (SEVERITY_RANK[x.severity]! - SEVERITY_RANK[y.severity]!) || (x.createdAt < y.createdAt ? 1 : -1)).slice(0, 6),
      },
      recent: rowsIn(period.from, period.to).map(decorate).filter((r) => ['spend', 'income'].includes(r.scope) && !r.internal).slice(0, 5),
      accounts: accountsData
        .filter((x) => x.assigned && x.creditStatus !== 'deleted')
        .sort((x, y) => order(x.type) - order(y.type))
        .map((x) => ({ account: accountRef(x), product: x.product, displayBalanceAgorot: x.displayBalanceAgorot, displayBalanceKind: x.displayBalanceKind, bookedInclLimitAgorot: x.balances.bookedInclLimit })),
      user: { id: state.user.id, displayName: state.user.displayName, role: state.user.role },
    };

    // The two REDESIGN payloads and the loans strip line come from the same code the server runs.
    const loans = loansResponse();
    const items = buildItems(urgencyInputsFor(base, loans, rowsIn(monthStart(CUR_MONTH), ASOF).filter((r) => r.returnKind !== null)), ASOF);
    const urgency: UrgencySummary = { items, groups: groupByBucket(items), counts: countByBucket(items) };
    const narrative: Narrative = buildNarrative(narrativeInputFor(base, items), ASOF);
    return {
      ...base,
      narrative,
      urgency,
      loans: { totals: loans.totals, endingSoon: loans.endingSoon, changeAgorot: loans.trend.changeAgorot },
    };
  }

  function order(type: Account['type']): number {
    return { CHECKING: 0, SAVINGS: 1, CARD: 2, LOAN: 3, SECURITY: 4 }[type];
  }

  function transactions(query: TransactionsQuery): TransactionsResponse {
    const period = query.from && query.to
      ? makePeriod(query.from, query.to, 'custom', null)
      : resolvePeriod(query.period);
    const cats = query.cat ? query.cat.split(',') : [];
    const limit = Math.min(200, Math.max(1, query.limit ?? 50));
    const offset = query.cursor ? Number(query.cursor) || 0 : 0;

    let list = rowsIn(period.from, period.to).map(decorate).filter((row) => {
      if (query.account && row.account.id !== query.account) return false;
      if (cats.length && !cats.includes(row.groupKey)) return false;
      if (query.status === 'pending' && row.status !== 'PENDING') return false;
      if (query.status === 'booked' && row.status !== 'BOOKED') return false;
      if (query.dir === 'credit' && (row.amountAgorot ?? 0) <= 0) return false;
      if (query.dir === 'debit' && (row.amountAgorot ?? 0) >= 0) return false;
      if (query.kind === 'returns' && !row.returnKind) return false;
      if (query.kind === 'internal' && !(row.internal || row.scope === 'transfer')) return false;
      if (query.kind && ['income', 'spend', 'card-charge'].includes(query.kind) && row.scope !== query.kind) return false;
      if (query.q) {
        const needle = query.q.toLowerCase();
        const hay = `${row.merchant} ${row.description} ${Math.abs((row.amountAgorot ?? 0) / 100).toFixed(2)}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return row.account.type === 'CHECKING' || row.account.type === 'CARD';
    });

    if (query.sort === 'amount') list = list.sort((x, y) => Math.abs(y.amountAgorot ?? 0) - Math.abs(x.amountAgorot ?? 0));

    const flow = list.filter((r) => ['spend', 'income'].includes(r.scope) && !r.internal);
    const page = list.slice(offset, offset + limit);
    const days = new Map<string, { debitAgorot: number; rows: number }>();
    for (const row of page) {
      const bucket = days.get(row.effDate) ?? { debitAgorot: 0, rows: 0 };
      days.set(row.effDate, bucket);
    }
    for (const row of list) {
      const bucket = days.get(row.effDate);
      if (!bucket) continue;
      bucket.rows += 1;
      if (['spend', 'income'].includes(row.scope) && !row.internal && (row.amountAgorot ?? 0) < 0) {
        bucket.debitAgorot += Math.abs(row.amountAgorot ?? 0);
      }
    }

    return {
      period,
      items: page,
      nextCursor: offset + limit < list.length ? String(offset + limit) : null,
      summary: {
        from: period.from, to: period.to,
        debitAgorot: flow.filter((r) => (r.amountAgorot ?? 0) < 0).reduce((s, r) => s + Math.abs(r.amountAgorot ?? 0), 0),
        creditAgorot: flow.filter((r) => r.scope === 'income').reduce((s, r) => s + (r.amountAgorot ?? 0), 0),
        count: flow.length,
        pendingCount: flow.filter((r) => r.status === 'PENDING').length,
        totalRows: list.length,
      },
      dayTotals: [...days.entries()].map(([day, v]) => ({ day, debitAgorot: v.debitAgorot, rows: v.rows })),
    };
  }

  function detail(id: string): TransactionDetail {
    const row = transactionsData.find((r) => r.id === id);
    if (!row) throw new ApiError('NOT_FOUND', 'העסקה לא נמצאה', 404);
    const decorated = decorate(row);
    const card = decorated.account.type === 'CARD'
      ? (decorated.account.id === ACC_MAX ? maxCard() : calCard())
      : null;
    const c = card?.current ?? null;
    return {
      ...decorated,
      installmentPlan: decorated.installment
        ? {
          merchant: decorated.merchant, merchantKey: decorated.merchantKey, totalCount: decorated.installment.total,
          postedCount: decorated.installment.n, remainingCount: decorated.installment.total - decorated.installment.n,
          amountAgorot: Math.abs(decorated.amountAgorot ?? 0),
          totalAgorot: Math.abs(decorated.amountAgorot ?? 0) * decorated.installment.total,
          nextDate: addDays(decorated.effDate, 30), nextDueDate: addDays(decorated.effDate, 35),
          lastDueMonth: addMonths(monthOf(decorated.effDate), decorated.installment.total - decorated.installment.n),
        }
        : null,
      related: [],
      cycle: c ? { start: c.start, end: c.end, dueDate: c.dueDate, status: c.status } : null,
    };
  }

  const api: Api = {
    login: (body) => {
      if (!body.email || !body.password) throw new ApiError('VALIDATION', 'נא למלא אימייל וסיסמה', 400);
      state.user = /michal/i.test(body.email) ? usersData[1]! : usersData[0]!;
      return delay({ user: state.user });
    },
    logout: () => delay({ ok: true as const }),
    me: () => delay({ user: state.user, appearance: state.appearance }),
    changePassword: () => delay({ ok: true as const }),

    summary: (period) => delay(summary(period)),

    loans: (query) => delay(loansResponse(query?.sort ?? 'rate')),

    transactions: (query) => delay(transactions(query)),
    transaction: (id) => delay(detail(id)),
    updateTransaction: (id, body) => {
      const row = transactionsData.find((r) => r.id === id);
      if (!row) throw new ApiError('NOT_FOUND', 'העסקה לא נמצאה', 404);
      let affected = 1;
      if (body.groupKey !== undefined) {
        if (body.groupKey === null) state.overrides.delete(id);
        else if (body.applyToMerchant) {
          const key = row.merchantKey;
          affected = 0;
          for (const other of transactionsData) {
            if (other.merchantKey !== key || other.scope !== 'spend') continue;
            state.overrides.set(other.id, body.groupKey);
            affected += 1;
          }
        } else state.overrides.set(id, body.groupKey);
      }
      if (body.note !== undefined) {
        if (body.note === null) state.notes.delete(id);
        else state.notes.set(id, body.note);
      }
      if (body.internal !== undefined) state.internal.set(id, body.internal);
      emit({ event: 'data:changed', data: { scopes: ['summary', 'transactions', 'categories'] } });
      return delay({ item: detail(id), affected });
    },

    categories: (periodParam) => {
      const period = resolvePeriod(periodParam);
      const months = trailingMonths(period.lastMonth, 6, '2025-09');
      const monthly = months.map((month) => {
        const byGroup = Object.fromEntries(GROUP_KEYS.map((k) => [k, monthSpend(month, k)])) as Record<GroupKey, number>;
        return { month, byGroup, totalAgorot: Object.values(byGroup).reduce((s, v) => s + v, 0), partial: month === CUR_MONTH };
      });
      const returns = months.map((month) => ({ month, count: month === CUR_MONTH ? 1 : month === '2026-04' ? 1 : 0, feesAgorot: month === CUR_MONTH ? a(38) : month === '2026-04' ? a(38) : 0 }));
      const response: CategoriesResponse = {
        period, items: sortedStats(period), monthly,
        returns: { months: returns, total: { count: returns.reduce((s, r) => s + r.count, 0), feesAgorot: returns.reduce((s, r) => s + r.feesAgorot, 0) } },
      };
      return delay(response);
    },
    category: (key, periodParam) => {
      if (!(GROUP_KEYS as readonly string[]).includes(key)) throw new ApiError('NOT_FOUND', 'קטגוריה לא נמצאה', 404);
      const period = resolvePeriod(periodParam);
      const stat = categoryStat(key, period);
      const months = trailingMonths(period.lastMonth, 6, '2025-09');
      const rows = rowsIn(period.lastFrom, period.lastTo).map(decorate).filter((r) => r.scope === 'spend' && r.groupKey === key);
      const total = rows.reduce((s, r) => s + Math.abs(r.amountAgorot ?? 0), 0) || 1;
      const subs = new Map<string | null, { amountAgorot: number; count: number; label: string | null }>();
      const merchants = new Map<string, { amountAgorot: number; count: number; merchantKey: string }>();
      for (const row of rows) {
        const sub = subs.get(row.category.sub) ?? { amountAgorot: 0, count: 0, label: row.category.sub === null ? null : categoryLabel(row.category.main, row.category.sub).sub };
        sub.amountAgorot += Math.abs(row.amountAgorot ?? 0);
        sub.count += 1;
        subs.set(row.category.sub, sub);
        const m = merchants.get(row.merchant) ?? { amountAgorot: 0, count: 0, merchantKey: row.merchantKey };
        m.amountAgorot += Math.abs(row.amountAgorot ?? 0);
        m.count += 1;
        merchants.set(row.merchant, m);
      }
      const response: CategoryDetailResponse = {
        period, stat,
        settings: state.categorySettings.get(key) ?? { groupKey: key, thresholdPct: null, manualBaselineAgorot: null, hidden: false, updatedBy: null, updatedAt: null },
        trend: months.map((month) => ({ month, amountAgorot: monthSpend(month, key), baselineAgorot: baselineOf(key, month).agorot || null, partial: month === CUR_MONTH })),
        subcategories: [...subs.entries()].map(([sub, v]) => ({ sub, label: v.label, amountAgorot: v.amountAgorot, share: v.amountAgorot / total, count: v.count })).sort((x, y) => y.amountAgorot - x.amountAgorot),
        merchants: [...merchants.entries()].map(([merchant, v]) => ({ merchant, merchantKey: v.merchantKey, amountAgorot: v.amountAgorot, count: v.count })).sort((x, y) => y.amountAgorot - x.amountAgorot).slice(0, 5),
      };
      return delay(response);
    },
    updateCategory: (key, body) => {
      const next: CategorySettings = {
        groupKey: key,
        thresholdPct: body.thresholdPct === undefined ? (state.categorySettings.get(key)?.thresholdPct ?? null) : body.thresholdPct,
        manualBaselineAgorot: body.manualBaselineAgorot === undefined ? (state.categorySettings.get(key)?.manualBaselineAgorot ?? null) : body.manualBaselineAgorot,
        hidden: body.hidden ?? state.categorySettings.get(key)?.hidden ?? false,
        updatedBy: state.user.displayName, updatedAt: SERVER_TIME,
      };
      if (next.thresholdPct === null && next.manualBaselineAgorot === null && !next.hidden) state.categorySettings.delete(key);
      else state.categorySettings.set(key, next);
      emit({ event: 'data:changed', data: { scopes: ['summary', 'categories'] } });
      return delay(next);
    },

    cards: () => {
      const response: CardsResponse = {
        asOf: ASOF,
        items: [maxCard(), calCard(), ...['acc_max_1207', 'acc_cal_0182', 'acc_max_3350', 'acc_cal_4416', 'acc_disc_5561'].map(closedCard)],
        coverage: coverage(), schedule: schedule(),
      };
      return delay(response);
    },
    card: (id) => {
      const card = id === ACC_MAX ? maxCard() : id === ACC_CAL ? calCard() : closedCard(id);
      const cycles = [card.lastCharged, card.final, card.current].filter((c): c is CardCycle => Boolean(c));
      const response: CardDetailResponse = {
        asOf: ASOF, card, cycles,
        installmentPlans: id === ACC_CAL
          ? [{ accountId: ACC_CAL, merchant: 'טיסות — אל על', merchantKey: 'טיסות אל על', totalCount: 6, postedCount: 2, remainingCount: 4, amountAgorot: a(620), totalAgorot: a(3720), nextDate: '2026-09-12', nextDueDate: '2026-10-04', lastDueMonth: '2027-01', firstDate: '2026-07-12', lastPostedDate: '2026-08-12', lastPostedN: 2, groupKey: 'OTHER' }]
          : [],
        schedule: schedule([id]),
        recent: transactionsData.filter((r) => r.account.id === id).slice(0, 5).map(decorate),
        issuerLoans: [],
      };
      return delay(response);
    },
    updateCard: (id, body) => {
      const settings = state.cardSettings.get(id) ?? { cutoffDay: 1, dueDay: 10 };
      if (body.cutoffDay !== undefined) settings.cutoffDay = body.cutoffDay;
      if (body.dueDay !== undefined) settings.dueDay = body.dueDay;
      state.cardSettings.set(id, settings);
      emit({ event: 'data:changed', data: { scopes: ['summary', 'cards', 'networth'] } });
      return api.card(id);
    },

    netWorth: () => delay(netWorth()),

    accounts: () => delay({ items: accountsData.map((x): Account => ({ ...x })) }),
    updateAccount: (id, body) => {
      const account = acc(id);
      if (body.assigned !== undefined) account.assigned = body.assigned;
      if (body.note) {
        account.note = {
          maturityDate: body.note.maturityDate ?? account.note?.maturityDate ?? null,
          remainingPayments: body.note.remainingPayments ?? account.note?.remainingPayments ?? null,
          monthlyPaymentAgorot: body.note.monthlyPaymentAgorot ?? account.note?.monthlyPaymentAgorot ?? null,
          note: body.note.note ?? account.note?.note ?? null,
          updatedBy: state.user.displayName, updatedAt: SERVER_TIME,
        };
      }
      emit({ event: 'data:changed', data: { scopes: ['summary', 'accounts', 'networth', 'cards', 'transactions'] } });
      return delay({ ...account });
    },
    connections: () => delay({ items: connectionsData }),

    settings: () => {
      const response: SettingsResponse = {
        alerts: { ...state.alertRules },
        alertDefaults: { cardDueDays: 2, thresholdPct: 20, largeTxAgorot: a(1500), staleHours: 30, disabled: [] },
        appearance: state.appearance,
        plan: { tier: 'starter', credits: 500 },
        sync: { everyMinutes: 360, lastRunAt: '2026-09-06T03:15:00.000Z', nextRunAt: '2026-09-06T09:15:00.000Z' },
        financyConfigured: true, users: usersData.length,
      };
      return delay(response);
    },
    updateSettings: (body) => {
      if (body.alerts) Object.assign(state.alertRules, body.alerts);
      if (body.appearance) Object.assign(state.appearance, body.appearance);
      emit({ event: 'data:changed', data: { scopes: ['settings', 'summary', 'categories'] } });
      return api.settings();
    },
    users: () => delay({ items: usersData, max: 2 }),

    sync: (providerRefresh) => {
      if (state.user.role !== 'owner') throw new ApiError('FORBIDDEN', 'רק יאיר יכול לרענן', 403, { required: 'owner' });
      state.syncRunId += 1;
      const runId = state.syncRunId;
      if (providerRefresh) state.creditsUsed += 20;
      emit({ event: 'sync:start', data: { runId, kind: providerRefresh ? 'refresh' : 'manual' } });
      setTimeout(() => {
        emit({
          event: 'sync:done',
          data: {
            run: { id: runId, kind: providerRefresh ? 'refresh' : 'manual', startedAt: SERVER_TIME, finishedAt: SERVER_TIME, ok: true, requestedBy: state.user.id, inserted: 4, updated: 9, superseded: 0, creditsUsed: providerRefresh ? 20 : 0, error: null },
            freshness: freshness(),
          },
        });
      }, 1400);
      return delay({ runId, kind: providerRefresh ? 'refresh' : 'manual', creditsEstimate: providerRefresh ? 20 : 0, startedAt: SERVER_TIME }, 60);
    },
    syncStatus: () => {
      const run = { id: state.syncRunId, kind: 'scheduled' as const, startedAt: '2026-09-06T03:15:00.000Z', finishedAt: '2026-09-06T03:15:42.000Z', ok: true, requestedBy: null, inserted: 12, updated: 3, superseded: 1, creditsUsed: 0, error: null };
      const response: SyncStatusResponse = {
        running: false, current: null, last: run, lastSuccessful: run,
        nextScheduledAt: '2026-09-06T09:15:00.000Z', freshness: freshness(),
        providerRefresh: { allowed: true, retryAt: null, reason: null, creditsEstimate: 20 },
      };
      return delay(response);
    },

    alerts: (query) => {
      const filter: AlertFilter = query?.filter ?? 'all';
      let list = query?.includeDismissed === '1' ? alertsData.map(alertView) : openAlerts();
      if (filter === 'unread') list = list.filter((al) => !al.readAt);
      else if (filter !== 'all') {
        const types = ALERT_FILTER_TYPES[filter];
        list = list.filter((al) => types.includes(al.type));
      }
      const response: AlertsResponse = {
        items: list.sort((x, y) => (x.createdAt < y.createdAt ? 1 : -1)),
        nextCursor: null, unreadCount: unreadAlerts().length, openCount: openAlerts().length,
      };
      return delay(response);
    },
    updateAlert: (id, body) => {
      const base = alertsData.find((al) => al.id === id);
      if (!base) throw new ApiError('NOT_FOUND', 'ההתראה לא נמצאה', 404);
      const current = alertView(base);
      state.alertState.set(id, {
        readAt: body.read === undefined ? current.readAt : body.read ? SERVER_TIME : null,
        dismissedAt: body.dismissed === undefined ? current.dismissedAt : body.dismissed ? SERVER_TIME : null,
      });
      emit({ event: 'alerts:changed', data: { unreadCount: unreadAlerts().length, openCount: openAlerts().length } });
      return delay(alertView(base));
    },
    readAllAlerts: () => {
      let updated = 0;
      for (const al of openAlerts()) {
        if (al.readAt) continue;
        state.alertState.set(al.id, { readAt: SERVER_TIME, dismissedAt: al.dismissedAt });
        updated += 1;
      }
      emit({ event: 'alerts:changed', data: { unreadCount: 0, openCount: openAlerts().length } });
      return delay({ updated, unreadCount: 0 });
    },

    manualEntries: () => delay({ items: manualEntries }),
    createManualEntry: (body) => {
      const entry: ManualEntry = body.kind === 'balance'
        ? { id: `man_${manualEntries.length + 1}`, kind: 'balance', side: body.side, label: body.label, balanceAgorot: body.balanceAgorot, asOf: body.asOf, note: body.note ?? null, createdBy: state.user.displayName, createdAt: SERVER_TIME }
        : { id: `man_${manualEntries.length + 1}`, kind: 'transaction', effDate: body.effDate, amountAgorot: body.amountAgorot, merchant: body.merchant, groupKey: body.groupKey, note: body.note ?? null, createdBy: state.user.displayName, createdAt: SERVER_TIME };
      manualEntries.push(entry);
      return delay(entry);
    },
    deleteManualEntry: (id) => {
      const index = manualEntries.findIndex((e) => e.id === id);
      if (index >= 0) manualEntries.splice(index, 1);
      return delay({ ok: true as const });
    },

    subscribe(onEvent) {
      listeners.add(onEvent);
      setTimeout(() => onEvent({ event: 'hello', data: { serverTime: SERVER_TIME, freshness: freshness() } }), 0);
      return () => {
        listeners.delete(onEvent);
      };
    },
    exportUrl: () => '#',
    isMock: true,
  };

  return api;
}
